import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type ComponentProps } from "react";
import { guestProfileCompletionFor, type GuestProfileCompletion } from "@/lib/guest-profile-completion";
import { Eye, EyeOff, Loader2, Mail, Info } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useLocation, useSearch } from "wouter";
import { isPrivateCheckoutEntry } from "@/lib/checkout-entry";
import { cn } from "@/lib/utils";
import { postAuthWouterDestination, safeRedirectPath } from "@/lib/safeRedirect";
import { clearCheckoutFlowPostAuthClaim } from "@/lib/checkout-state";
import { useRecoveryMode } from "@/lib/auth/recoveryMode";
import { useUser } from "@/hooks/useUser";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useSupport } from "@/hooks/useSupport";
import { sendMagicLinkBypass } from "@/lib/users-api";
import { track } from "@/lib/analytics";
import { getRememberMePreference, setRememberMePreference } from "@/lib/session-policy";
import { useToast } from "@/hooks/use-toast";
import { GoogleLogo, AppleLogo } from "@/components/ui/brand-icons";
import { DsIcon } from "@/components/ui/ds-icons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ModalShell } from "@/components/ui/modal-shell";
import { LiveStatus } from "@/components/ui/live-status";
import { Logo } from "@/components/platform/Logo";
import { HostMfaNudge } from "@/components/auth/HostMfaNudge";
import { DriveShareLoginNudgeWatcher } from "@/components/auth/DriveShareLoginModal";
import { createOtpJourneyMetadata, type OtpJourneyMetadata } from "@/lib/auth/otp-journey";
import { proxyAuthTestOtp } from "@/lib/auth-proxy";
import { checkoutHandoffContext } from "@/lib/analytics/checkout-diagnostics";

// Stricter email validation than the loose `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`
// pattern we used previously. The old regex accepted typos like
// `ben@gti.im\.` (trailing backslash + dot) which then sailed through
// to Supabase, burned an OTP send against our hourly quota, and left
// the user staring at "We sent a code to ben@gti.im\." on the verify
// step. This validator:
//   - rejects whitespace, backslashes, quotes, commas
//   - requires at least one literal "." in the domain
//   - requires a 2+ alpha-only TLD (so `gti.im\.` fails, `gti.im` passes)
//   - allows the standard `+` aliasing and dotted-local addresses
const EMAIL_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;
// Render a "Mm Ss" / "Ss" duration string for the rate-limit cooldown
// banner. Server returns `retryAfter` in seconds, and we localize the
// final phrase via i18n keys (`auth.rate_limited_*` + `auth.try_again_in`).
function formatRetryAfterDuration(seconds: number): string {
  const s = Math.max(1, Math.ceil(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

// Pull the `retryAfter` (seconds) off the standard rate-limited Error
// object produced by `rateLimitedAuthError` in `lib/auth-proxy.ts`. We
// recognize it by `code === "rate_limited"` so callers don't have to
// parse the message string.
function rateLimitRetryAfter(err: { code?: string; retryAfter?: number } | null | undefined): number | null {
  if (!err) return null;
  if (err.code !== "rate_limited") return null;
  return typeof err.retryAfter === "number" ? err.retryAfter : null;
}

function isValidEmail(raw: string): boolean {
  const v = raw.trim();
  if (v.length < 6 || v.length > 254) return false;
  if (/[\s\\"',]/.test(v)) return false;
  return EMAIL_RE.test(v);
}

import { POST_AUTH_REDIRECT_KEY, rememberPostAuthNext } from "@/lib/post-auth-continuation";
export { POST_AUTH_REDIRECT_KEY };

// Task #5603 — OTP send throttling.
// `OTP_SEND_DEDUPE_MS`: window in which a NON-forced re-send of the same
// address is suppressed (the still-valid code isn't rotated out). Covers the
// Back → re-submit / double-tap / StrictMode double-invoke paths.
const OTP_SEND_DEDUPE_MS = 20_000;
// `OTP_RESEND_COOLDOWN_MS`: min gap the "Send a new code" button is held after
// an explicit (forced) resend, so users can't mint back-to-back superseding
// codes. Surfaced as a live countdown on the button.
const OTP_RESEND_COOLDOWN_MS = 30_000;

type AuthModalIntent = "signin" | "signup" | "auto";
type AuthModalContextHint = "default" | "list-a-car" | "reserve-car";

interface OpenOptions {
  intent?: AuthModalIntent;
  next?: string;
  prefilledEmail?: string;
  context?: AuthModalContextHint;
  // Task #2451 — allow opening the modal directly into a specific step
  // (used by SoftDeletedAutoOpener so OAuth + password re-sign-ins land
  // on the restore screen without forcing the user to re-enter email).
  initialStep?: Step;
}

type Step =
  | "email"
  | "code"
  | "password"
  | "social-hint"
  | "signup"
  | "magic-sent"
  | "signup-confirm"
  | "verify-pending"
  // Task #2451 — shown after OTP verify when the email matched a
  // soft-deleted account still inside the 30-day retention window.
  // Offers a single "Restore" CTA (and a quiet "Cancel" that signs out).
  | "soft-deleted"
  // Task #4226 — Facebook sign-in has been retired. Clicking the (still
  // visible) Facebook button no longer starts Facebook OAuth; it routes
  // here, to an on-brand explanation that points returning Facebook users
  // at the email address tied to their account, with a support escape hatch.
  | "facebook-retired";

interface LookupResult {
  exists: boolean;
  providers: string[];
  unconfirmed?: boolean;
}

const SOCIAL_PROVIDERS = ["google", "facebook", "apple"] as const;
type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

function isSocialProvider(p: string): p is SocialProvider {
  return (SOCIAL_PROVIDERS as readonly string[]).includes(p);
}

function providerLabel(p: string, t: (k: string) => string): string {
  switch (p) {
    case "google":
      return t("auth.continue_google");
    case "facebook":
      return t("auth.continue_facebook");
    case "apple":
      return t("auth.continue_apple");
    case "email":
      return t("auth.email");
    default:
      return p;
  }
}

/**
 * AuthModalInternals — the heavy auth modal UI, lazy-loaded by
 * AuthModalProvider (defined in AuthModalContext.tsx). Wraps all the
 * components the provider used to render inline so they can be split
 * into a separate chunk and kept out of the critical entry bundle.
 */
export function AuthModalInternals({
  isOpen,
  onClose,
  intent,
  next,
  prefilled,
  contextHint,
  initialStep,
  open,
}: {
  isOpen: boolean;
  onClose: () => void;
  intent: AuthModalIntent;
  next: string;
  prefilled: string;
  contextHint: AuthModalContextHint;
  initialStep?: Step;
  open: (opts?: OpenOptions) => void;
}) {
  // Task #4765 — the DriveShare login nudge takes precedence over the blocking
  // ProfileCompletionDialog for DriveShare-origin users (who are typically
  // mid-migration with an incomplete BAC profile). The watcher reports when it
  // owns the screen so we defer the completion dialog and never stack two
  // modals; once the user dismisses the nudge, the completion dialog renders.
  const [driveShareNudgeActive, setDriveShareNudgeActive] = useState(false);
  const [authLocation] = useLocation();
  const authSearch = useSearch();
  const { user: checkoutPrincipal } = useUser();
  const inlineCheckout = authLocation.replace(/\/+$/, "") === "/checkout" &&
    !isPrivateCheckoutEntry(authSearch, checkoutPrincipal?.id);
  const cancelAuth = useCallback(() => {
    if (contextHint === "reserve-car") {
      try {
        const flowId = new URL(next, window.location.origin).searchParams.get("h");
        if (flowId) clearCheckoutFlowPostAuthClaim(flowId);
      } catch {
        // An invalid destination is rejected elsewhere; there is no claim to retain.
      }
    }
    onClose();
  }, [contextHint, next, onClose]);
  return (
    <>
      <AuthModalRoot
        isOpen={isOpen}
        onClose={onClose}
        onCancel={cancelAuth}
        intent={intent}
        next={next}
        prefilled={prefilled}
        contextHint={contextHint}
        initialStep={initialStep}
      />
      <SoftDeletedAutoOpener isOpen={isOpen} open={open} />
      <ProfileCompletionWatcher deferForDriveShare={driveShareNudgeActive} />
      <PostAuthRedirector />
      {/* Task #4640 / #4765 — post-login nudge for DriveShare-origin users.
          Fully inert when feature_driveshare_login_redirect is OFF; never
          blocks login. Reports `active` so the completion dialog defers. */}
      {!inlineCheckout && <DriveShareLoginNudgeWatcher onActiveChange={setDriveShareNudgeActive} />}
      {/* Task #3063 — host MFA nudge mounts inside AuthModalProvider so it
          sits above every page and has access to the session via useUser(). */}
      <HostMfaNudge />
    </>
  );
}

/**
 * Task #2451 — Global watcher that surfaces the soft-deleted restore
 * UX regardless of which sign-in path produced the session. The OTP
 * flow keeps its in-modal watcher (AuthModalRoot's
 * `awaitingSoftDeletedCheck`) because the modal is still open when
 * the verdict arrives. But for the password and OAuth paths the modal
 * has already closed (or never opened on this tab), so we re-open it
 * here directly into the restore step as soon as AuthProvider reports
 * `softDeletedInfo` from its /auth/ensure-user response. The user is
 * authenticated at this point (they proved possession of the
 * inbox/identity), which is what makes disclosing the soft-deleted
 * state safe.
 */
function SoftDeletedAutoOpener({ isOpen, open }: { isOpen: boolean; open: (opts?: OpenOptions) => void }) {
  const { softDeletedInfo } = useUser();
  useEffect(() => {
    if (softDeletedInfo && !isOpen) {
      open({ initialStep: "soft-deleted" });
    }
  }, [softDeletedInfo, isOpen, open]);
  return null;
}

interface RootProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  intent: AuthModalIntent;
  next: string;
  prefilled: string;
  contextHint: AuthModalContextHint;
  initialStep?: Step;
}

function AuthModalRoot({ isOpen, onClose, onCancel, intent, next, prefilled, contextHint, initialStep }: RootProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { signInWithEmail, signUpWithEmail, signInWithOAuth, signInWithOtp, verifyEmailOtp, resetPassword, softDeletedInfo, restoreAccount, signOut, session, profile } = useUser();
  // Clicking the brand logo at the top of the modal acts as a shortcut home:
  // close the modal (so neither it nor its backdrop stays mounted) then
  // navigate to "/" via wouter's client-side routing.
  const goHomeFromLogo = useCallback(() => {
    onCancel();
    setLocation("/");
  }, [onCancel, setLocation]);
  // Track the session via a ref so the OTP verify race-suppression check
  // below can read the *latest* value without re-creating submitOtpCode on
  // every auth state change (and inadvertently re-triggering CodeStep's
  // auto-submit effect).
  const sessionRef = useRef(session);
  const otpJourneyProofRef = useRef<string | null>(null);
  const checkoutHandoff = useMemo(() => checkoutHandoffContext(next), [next]);
  const trackCheckoutAuth = useCallback((
    outcome: "succeeded" | "failed",
    method: "otp" | "password" | "oauth",
    reason?: "invalid_credentials" | "rate_limited" | "expired_or_superseded" | "mismatch" | "client_timeout" | "provider_error",
  ) => {
    if (!checkoutHandoff) return;
    track("checkout_auth_handoff", {
      checkout_journey_id: checkoutHandoff.checkoutJourneyId,
      car_id: checkoutHandoff.carId,
      stage: "authentication",
      outcome,
      method,
      reason,
    });
  }, [checkoutHandoff]);
  const otpTestMagicLinkTokenHashRef = useRef<string | null>(null);
  useEffect(() => { sessionRef.current = session; }, [session]);
  // Task #2451 — after OTP verify, AuthProvider may surface a
  // soft-deleted match. We only want that to drive the restore step
  // *for this in-flight sign-in* (not for a tab that loaded with a
  // pre-existing soft-deleted session), so we arm the watcher only
  // when the user is on the OTP step.
  const [awaitingSoftDeletedCheck, setAwaitingSoftDeletedCheck] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [marketingOptOut, setMarketingOptOut] = useState(false);
  // Task #3061 — Remember-me state seeds from the persisted preference
  // so a returning visitor sees the last choice pre-applied. Writes go
  // through `setRememberMePreference` (see `onRememberMeChange` below)
  // so the Supabase client's storage adapter sees the new value BEFORE
  // we trigger sign-in.
  const [rememberMe, setRememberMe] = useState<boolean>(() => getRememberMePreference());
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Password-reset feedback is tracked SEPARATELY from the shared `error`
  // banner. The reset email-send limiter uses a long 1-hour window, so its
  // cooldown retry-after can be tens of minutes (~58 min observed). Routing
  // that into the shared `error` made it render on the "Continue with
  // password" screen as if password login itself were locked for an hour —
  // when in fact the user could still sign in with their password. Keeping
  // reset feedback in its own state means a reset cooldown can never bleed
  // onto (or block) the password-login surface.
  const [resetError, setResetError] = useState<string | null>(null);
  // In-flight flag for the "Forgot password?" action only — kept apart from the
  // shared `loading` so a reset send doesn't spin the password-login button.
  const [resetBusy, setResetBusy] = useState(false);
  // Bumped on every Forgot click so a repeated press while throttled still
  // re-acknowledges visibly even when the message text is unchanged (the notice
  // is keyed on this so React remounts + re-announces it).
  const [resetAttempt, setResetAttempt] = useState(0);
  const [lookup, setLookup] = useState<LookupResult | null>(null);
  const [duplicateAccount, setDuplicateAccount] = useState(false);
  // Set when Supabase's OTP send is blocked by its per-email / per-IP rate
  // limit (server-side; returns instantly from in-memory counter). We surface
  // a "Sign in with password instead" escape hatch so users with a password
  // are never trapped behind the cooldown window. Cleared on email edit.
  const [otpRateLimited, setOtpRateLimited] = useState(false);
  // Background OTP send in flight after optimistically advancing to the
  // code screen. Distinct from `loading` (which gates code *verification*)
  // so the code input stays usable while the send completes.
  const [sendingCode, setSendingCode] = useState(false);

  const isTestOtpEmail = useCallback(
    (value: string) =>
      import.meta.env.DEV && /^otp[a-z0-9-]+@e2e\.test$/i.test(value.trim().toLowerCase()),
    [],
  );
  // Magic-link bypass: when Supabase's /otp is rate-limited, the user can
  // request a sign-in link minted server-side via our admin client and
  // delivered through Resend (POST /auth/send-magic-link). This bypasses
  // the GoTrue email-send pool entirely. After a successful send we show
  // a "Check your email" confirmation in place of the buttons so the user
  // doesn't spam-click.
  const [magicLinkBypassSent, setMagicLinkBypassSent] = useState(false);
  const [sendingBypassLink, setSendingBypassLink] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  // Single-flight guards for OTP verify. `submittedCodeRef` records the
  // last cleaned 6-digit value we attempted to verify so duplicate triggers
  // (auto-submit effect, form onSubmit, Enter key, browser one-time-code
  // autofill, StrictMode double-invoke) collapse into a single Supabase
  // verifyOtp call. `verifyingRef` short-circuits any caller that arrives
  // while a verify is mid-flight. Both live in the parent so they survive
  // a CodeStep remount under React 18 StrictMode (#2581).
  const submittedCodeRef = useRef<string>("");
  const verifyingRef = useRef<boolean>(false);
  // Single-flight guards for OTP *sends* (Task #5603). The root cause of the
  // "that code has expired" reports is a second OTP send silently superseding
  // the code already racing to the user's inbox: Supabase invalidates the
  // previous code the instant a new one is minted. `sendInFlightRef` collapses
  // overlapping sends (double-submit, StrictMode double-invoke, Enter+click),
  // and `lastSentRef` suppresses an accidental *re-send* of the same address
  // within a short window (e.g. going Back to the email step and re-submitting)
  // so the fresh code the user is about to type is never rotated out from under
  // them. An explicit "Send a new code" (force) always sends.
  const sendInFlightRef = useRef<boolean>(false);
  const lastSentRef = useRef<{ email: string; at: number }>({ email: "", at: 0 });
  // Explicit-resend cooldown: after a forced resend we hold the button for a
  // few seconds so rapid taps can't mint back-to-back codes that supersede one
  // another. Timestamp (ms) until which resend is disabled; 0 = ready.
  const [resendCooldownUntil, setResendCooldownUntil] = useState<number>(0);
  // Set when the code-screen recovery path fell back to the admin-minted
  // sign-in link (delivered via Resend) because the OTP send was rate-limited.
  const [bypassLinkSent, setBypassLinkSent] = useState<boolean>(false);
  // Task #6311 — set when the server deduped a repeat request because a
  // still-valid code was sent moments ago (no new email went out). Rendered
  // on the code screen as guidance, not an error.
  const [alreadySentNotice, setAlreadySentNotice] = useState<boolean>(false);
  // The email step advances optimistically, so only claim delivery after the
  // proxy confirms that a code is in flight or a send completed successfully.
  const [otpSendConfirmed, setOtpSendConfirmed] = useState(false);
  const otpRateLimitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A modal opening is one opaque OTP journey. Keeping it in a ref makes
  // retries/resends part of the same funnel denominator without ever tying
  // analytics to the renter's email address.
  const otpJourneyRef = useRef<OtpJourneyMetadata>(createOtpJourneyMetadata(next));
  // Task #6917 — show a one-time notice when the user lands here after an
  // automatic session-expired redirect (?reason=session_expired). Dismissed
  // as soon as the user starts typing so it doesn't block sign-in.
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState<boolean>(() => {
    try {
      return new URLSearchParams(window.location.search).get("reason") === "session_expired";
    } catch {
      return false;
    }
  });

  // Reset whenever the dialog opens/closes.
  useEffect(() => {
    if (isOpen) {
      // Task #2451 — when opened by SoftDeletedAutoOpener, land
      // directly on the restore step instead of resetting to "email".
      setStep(initialStep ?? "email");
      setEmail(prefilled);
      setPassword("");
      setOtpCode("");
      setShowPassword(false);
      setFirstName("");
      setLastName("");
      setMarketingOptOut(false);
      setAcceptTerms(false);
      setError(null);
      setLookup(null);
      setDuplicateAccount(false);
      setLoading(false);
      setResetError(null);
      setOtpRateLimited(false);
      setSendingCode(false);
      setMagicLinkBypassSent(false);
      setSendingBypassLink(false);
      setResendCooldownUntil(0);
      setBypassLinkSent(false);
      setAlreadySentNotice(false);
      setOtpSendConfirmed(false);
      if (otpRateLimitTimeoutRef.current) {
        clearTimeout(otpRateLimitTimeoutRef.current);
        otpRateLimitTimeoutRef.current = null;
      }
      otpJourneyRef.current = createOtpJourneyMetadata(next);
      submittedCodeRef.current = "";
      verifyingRef.current = false;
      sendInFlightRef.current = false;
      lastSentRef.current = { email: "", at: 0 };
      // focus email after the dialog content mounts
      setTimeout(() => emailInputRef.current?.focus(), 50);
    }
  }, [isOpen, prefilled]);

  useEffect(() => () => {
    if (otpRateLimitTimeoutRef.current) clearTimeout(otpRateLimitTimeoutRef.current);
  }, []);

  const goBackToEmail = useCallback(() => {
    setStep("email");
    setError(null);
    setResetError(null);
    setPassword("");
    setOtpCode("");
    setLookup(null);
    setDuplicateAccount(false);
    submittedCodeRef.current = "";
    verifyingRef.current = false;
    setLoading(false);
  }, []);

  const switchToPassword = useCallback(() => {
    setError(null);
    setResetError(null);
    setPassword("");
    setLookup({ exists: true, providers: ["password"] });
    setStep("password");
  }, []);

  const switchToLogin = useCallback(() => {
    setError(null);
    setResetError(null);
    setPassword("");
    setDuplicateAccount(false);
    setLookup({ exists: true, providers: ["email"] });
    setStep("password");
  }, []);

  const apiBase = import.meta.env.BASE_URL || "/";

  // Send a sign-in link via our admin-minted Resend transport, bypassing
  // the Supabase /otp rate limit entirely. Only surfaced after the OTP
  // path itself has been rate-limited. We don't gate on email validity
  // here because the EmailStep button is only enabled when the trimmed
  // address has already passed `isValidEmail`.
  const requestBypassMagicLink = useCallback(async () => {
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) return;
    setError(null);
    setSendingBypassLink(true);
    try {
      const res = await sendMagicLinkBypass(trimmed, next || undefined);
      if (res.ok) {
        setMagicLinkBypassSent(true);
      } else if (res.rateLimited) {
        // Our own per-email/per-IP limit tripped — different message than
        // Supabase's "too many", because the cooldown shape is different.
        setError(t("auth.too_many_attempts") || "Too many attempts. Please try again later.");
      } else {
        setError(t("auth.magic_link_send_failed") || "Couldn't send the sign-in link. Please try again later.");
      }
    } catch {
      setError(t("auth.magic_link_send_failed") || "Couldn't send the sign-in link. Please try again later.");
    } finally {
      setSendingBypassLink(false);
    }
  }, [email, next, t]);

  // Shared OTP send used by both the optimistic email-step advance and the
  // code-screen resend. On failure it sets the same specific error/rate-limit
  // states so the code screen surfaces consistent messaging; returns true on
  // success so callers (e.g. resend) can confirm the send.
  //
  // Airbnb-style: send a 6-digit email OTP for everyone (existing or new),
  // regardless of `intent`. The legacy name/password/terms form (step=
  // "signup") is no longer reachable from the email step because Supabase's
  // signUpWithEmail triggers the "Confirm your email" link template instead of
  // the OTP template — we want a single, consistent 6-digit-code experience.
  // Brand-new users still get name + terms captured by ProfileCompletionDialog
  // after they verify the code. Supabase's signInWithOtp creates the user if
  // missing (shouldCreateUser defaults true), and the same email contains both
  // a magic-link AND a numeric token. Task #1605 anti-enumeration is preserved
  // because we don't reveal whether the address exists — the response shape is
  // identical for both paths.
  const sendOtp = useCallback(
    async (
      targetEmail: string,
      opts?: { force?: boolean },
    ): Promise<{ ok: boolean; rateLimited: boolean }> => {
      const force = opts?.force === true;
      // Single-flight: never let two sends overlap. A second in-flight send
      // makes Supabase mint a new code that silently invalidates the one
      // already on its way to the inbox — the exact "that code expired" bug
      // (Task #5603). A caller that arrives mid-send is a no-op.
      if (sendInFlightRef.current) return { ok: false, rateLimited: false };
      // Dedupe an accidental *re-send* of the same address inside a short
      // window (Back → re-submit, StrictMode double-invoke, double-tap). The
      // code we already emailed is still valid, so treat this as a successful
      // no-op instead of superseding it. Explicit resend (force) skips this.
      const now = Date.now();
      if (
        !force &&
        lastSentRef.current.email === targetEmail &&
        now - lastSentRef.current.at < OTP_SEND_DEDUPE_MS
      ) {
        setError(null);
        setOtpRateLimited(false);
        setOtpSendConfirmed(true);
        return { ok: true, rateLimited: false };
      }
      sendInFlightRef.current = true;
      if (otpRateLimitTimeoutRef.current) {
        clearTimeout(otpRateLimitTimeoutRef.current);
        otpRateLimitTimeoutRef.current = null;
      }
      setError(null);
      setOtpRateLimited(false);
      setSendingCode(true);
      try {
        const otpRes = await signInWithOtp(targetEmail, next || undefined, {
          force,
          journey: otpJourneyRef.current,
          testDelivery: isTestOtpEmail(targetEmail),
        });
        const otpError = otpRes.error;
        if (otpError) {
          // Server-enforced cooldown (rate-limited proxy) takes precedence —
          // surface the exact seconds-until-retry so the user knows when they
          // can try again, instead of a generic "too many" string.
          const retryAfter = rateLimitRetryAfter(otpError as { code?: string; retryAfter?: number });
          if (retryAfter !== null) {
            const retryMessage = t("auth.rate_limited_send", {
              defaultValue: "Too many code requests. Please wait {{time}} before trying again.",
              time: formatRetryAfterDuration(retryAfter),
            });
            setOtpRateLimited(true);
            setError(retryMessage);
            // Retry-After guidance describes a finite cooldown; don't leave
            // the user looking at an expired wait message or lockout affordance.
            otpRateLimitTimeoutRef.current = setTimeout(() => {
              setOtpRateLimited(false);
              setError((current) => current === retryMessage ? null : current);
              otpRateLimitTimeoutRef.current = null;
            }, Math.max(1, Math.ceil(retryAfter)) * 1000);
            track("auth_otp_rate_limited", { surface: "modal", retry_after_sec: retryAfter });
            track("auth_otp_client_cooldown_shown", {
              ...otpJourneyRef.current,
              cooldown_kind: "server_enforced",
            });
            return { ok: false, rateLimited: true };
          }
          const errCode = (otpError as { code?: string }).code;
          const msg = (otpError.message || "").toLowerCase();
          if (errCode === "unavailable") {
            // Limiter infrastructure is unavailable, not rate-limited. Keep
            // immediate retries available and never invoke the rate-limit
            // recovery-link bypass for this infrastructure failure.
            setOtpRateLimited(false);
            setError(t("auth.otp_unavailable", {
              defaultValue: "Code requests are temporarily unavailable. Please try again shortly.",
            }));
            return { ok: false, rateLimited: false };
          } else if (errCode === "transport") {
            // Cold-start 502 / network blip / proxy hiccup. Nothing was
            // actually sent to Supabase, so don't lock the user into a
            // rate-limit cooldown UI — tell them the server's flaky and let
            // them retry immediately.
            setOtpRateLimited(false);
            setError(
              t("auth.transient_send_failure", {
                defaultValue: "We couldn't reach the server. Please try again in a moment.",
              }),
            );
            return { ok: false, rateLimited: false };
          } else if (msg.includes("rate") || msg.includes("too many")) {
            // Supabase-side rate limit (no retryAfter we can trust) — fall
            // back to the legacy generic phrasing.
            setOtpRateLimited(true);
            setError(t("auth.too_many_attempts") || "Too many attempts. Please try again later.");
            return { ok: false, rateLimited: true };
          } else {
            setOtpRateLimited(false);
            setError(otpError.message || t("auth.magic_link_failed") || "Couldn't send code. Please try again.");
            return { ok: false, rateLimited: false };
          }
        }
        setOtpRateLimited(false);
        setError(null);
        setOtpSendConfirmed(true);
        lastSentRef.current = { email: targetEmail, at: Date.now() };
        if (otpRes.otpJourneyProof) otpJourneyProofRef.current = otpRes.otpJourneyProof;
        // In a development E2E run, exchange the opaque receipt for the
        // synthetic mailbox code. This never runs for normal addresses or
        // production bundles and never exposes a provider-delivered code.
        if (otpRes.otpTestReceipt) {
          const testDelivery = await proxyAuthTestOtp(otpRes.otpTestReceipt);
          if (testDelivery) {
            otpTestMagicLinkTokenHashRef.current = testDelivery.magicLinkTokenHash;
            setOtpCode(testDelivery.code);
          }
        }
        if (otpRes.alreadySent) {
          // Task #6311 — the server kept the code already in flight instead
          // of minting a superseding one. Show guidance (not an error) and
          // hold the resend button for the remainder of the send window.
          setAlreadySentNotice(true);
          const holdMs = Math.max(
            OTP_RESEND_COOLDOWN_MS,
            (otpRes.retryAfterSec ?? 0) * 1000,
          );
          setResendCooldownUntil((prev) => Math.max(prev, Date.now() + holdMs));
          track("auth_otp_already_sent_notice", { surface: "modal" });
          track("auth_otp_client_cooldown_shown", {
            ...otpJourneyRef.current,
            cooldown_kind: "local_resend",
          });
        } else {
          setAlreadySentNotice(false);
          // Task #6311 — arm the resend countdown from the FIRST successful
          // send too, so the "Send a new code" affordance always communicates
          // that the just-sent code needs a moment to arrive.
          setResendCooldownUntil((prev) => Math.max(prev, Date.now() + OTP_RESEND_COOLDOWN_MS));
          track(force ? "auth_otp_resent_client" : "auth_otp_requested_client", { surface: "modal" });
          track("auth_otp_client_cooldown_shown", {
            ...otpJourneyRef.current,
            cooldown_kind: "local_resend",
          });
        }
        return { ok: true, rateLimited: false };
      } catch {
        setOtpRateLimited(false);
        setError(t("auth.lookup_failed") || "Something went wrong. Please try again.");
        return { ok: false, rateLimited: false };
      } finally {
        setSendingCode(false);
        sendInFlightRef.current = false;
      }
    },
    [isTestOtpEmail, signInWithOtp, next, t],
  );

  const submitEmailStep = useCallback(() => {
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setError(t("auth.invalid_email") || "Please enter a valid email");
      return;
    }
    // Advance to the code-entry screen immediately on a valid email, then fire
    // the OTP send in the background. The previous flow awaited the send (and
    // any server cold-start) before transitioning, which froze the modal on a
    // spinner. Anti-enumeration is preserved because we always advance
    // regardless of whether the address exists. A failed send surfaces its
    // specific error on the code screen (with retry), and the user can go back
    // to the email step at any time.
    setEmail(trimmed);
    setOtpCode("");
    otpTestMagicLinkTokenHashRef.current = null;
    setOtpRateLimited(false);
    setOtpSendConfirmed(false);
    setAlreadySentNotice(false);
    setLookup({ exists: true, providers: ["password"], unconfirmed: false });
    setStep("code");
    void sendOtp(trimmed);
  }, [email, sendOtp, t]);

  const submitOtpCode = useCallback(async () => {
    const cleaned = otpCode.replace(/\D/g, "");
    if (cleaned.length !== 6) {
      setError(t("auth.otp_invalid", { defaultValue: "Enter the 6-digit code from your email." }));
      return;
    }
    // Single-flight guard (#2581). Any duplicate trigger for the same
    // 6-digit value — or any trigger that arrives while a verify is still
    // in-flight — short-circuits silently. Without this, the second
    // verifyOtp call would race the first, hit Supabase *after* the code
    // was already consumed, and surface a spurious "didn't match" /
    // "expired" error even though sign-in succeeded.
    if (loading) return;
    if (verifyingRef.current) return;
    if (submittedCodeRef.current === cleaned) return;
    submittedCodeRef.current = cleaned;
    verifyingRef.current = true;
    setError(null);
    setLoading(true);
    let verifyErr: Error | null = null;
    try {
      const res = await verifyEmailOtp(email, cleaned, {
        journey: otpJourneyRef.current,
        journeyProof: otpJourneyProofRef.current ?? undefined,
        testMagicLinkTokenHash: otpTestMagicLinkTokenHashRef.current ?? undefined,
      });
      verifyErr = res.error;
    } catch (e) {
      // verifyEmailOtp wraps Supabase's error in `{ error }`, but guard
      // against an unexpected runtime/network throw so the guards/loading
      // state never get stuck and lock the user out of retrying (#2581).
      verifyErr = e instanceof Error ? e : new Error(String(e));
    } finally {
      // NOTE: loading is intentionally NOT cleared here. On success we
      // want the spinner / disabled state to persist through the
      // post-verify profile fetch (#3097), all the way until the modal
      // closes or transitions to the soft-deleted step. Failure paths
      // below clear loading explicitly before returning so retry works.
      verifyingRef.current = false;
    }
    if (verifyErr) {
      // Defense-in-depth: if a session for this email is already present,
      // our own first verifyOtp call already succeeded and consumed the
      // code; this failure is the losing side of the race, not a genuine
      // user-facing error (#2581). Leave submittedCodeRef set so further
      // retries of the same code are still suppressed.
      const liveEmail = sessionRef.current?.user?.email?.toLowerCase() || "";
      if (liveEmail && liveEmail === email.trim().toLowerCase()) {
        // fall through to the success path
      } else {
        // Allow the user to retry by re-entering the code: clear the
        // submitted-code latch so the next attempt isn't deduped.
        submittedCodeRef.current = "";
        setLoading(false);
        const msg = (verifyErr.message || "").toLowerCase();
        track("auth_otp_verify_failed", {
          surface: "modal",
          reason: msg.includes("expired") ? "expired_or_superseded" : "mismatch",
        });
        track("auth_otp_verification_failed", {
          ...otpJourneyRef.current,
          reason: msg.includes("expired") ? "expired_or_superseded" : "mismatch",
        });
        trackCheckoutAuth(
          "failed",
          "otp",
          msg.includes("expired") ? "expired_or_superseded" : "mismatch",
        );
        if (msg.includes("expired")) {
          // Supabase returns "Token has expired or is invalid" for BOTH an
          // expired code AND one that was superseded by a newer send, so the
          // copy stays deliberately calm and points at the most recent email
          // rather than blaming the user (Task #5603).
          setError(t("auth.otp_expired", { defaultValue: "That code didn't work. It may have expired or a newer code was sent. Enter the most recent code from your email, or tap \"Send a new code\"." }));
        } else {
          setError(t("auth.otp_wrong", { defaultValue: "That code didn't match. Use the most recent code we emailed you, then try again." }));
        }
        return;
      }
    }
    // The verifyEmailOtp call above synchronously fires Supabase's
    // SIGNED_IN event, which AuthProvider handles by clearing every
    // `ensure_user_cache:*` key (cross-user safety) and re-fetching the
    // profile via /auth/ensure-user. We just need to:
    //   1. stash the optional `next` URL so PostAuthRedirector can read
    //      it once the profile arrives and is_complete is true,
    //   2. close the modal so ProfileCompletionWatcher (mounted inside
    //      AuthModalProvider) can take over rendering the dialog when
    //      the freshly-loaded profile is incomplete.
    // Crucially we do NOT poll sessionStorage on a setTimeout — the old
    // 800ms timer raced with AuthProvider's ensure-user round-trip and
    // routinely either forced a manual page refresh (#1752 defect 1) OR
    // navigated away before ProfileCompletionDialog could mount.
    // Task #2451 — arm the soft-deleted watcher *before* closing. The
    // AuthProvider's SIGNED_IN handler will fetch /auth/ensure-user
    // asynchronously and either populate `profile` (normal path) or
    // `softDeletedInfo` (re-registering inside the GDPR window). The
    // useEffect below routes us into the restore step in the latter
    // case. We don't close the modal here; the watcher does it once
    // the verdict is in.
    track("auth_otp_verify_succeeded", { surface: "modal" });
    track("auth_otp_verification_completed", otpJourneyRef.current);
    setAwaitingSoftDeletedCheck(true);
    rememberPostAuthNext(next);
  }, [otpCode, email, verifyEmailOtp, t, next, onClose, trackCheckoutAuth]);

  // Resolve the post-OTP verdict (soft-deleted match vs normal sign-in).
  // The ensure-user round-trip resolves into one of three branches:
  //   1. softDeletedInfo present -> route to the restore step (below),
  //   2. profile present (normal sign-in) -> close immediately (below),
  //   3. neither within ~5s (slow/failed ensure-user) -> fall through to
  //      the fallback timeout close so the user is never trapped behind a
  //      stuck spinner (the SIGNED_IN handler is still in flight;
  //      ProfileCompletionWatcher and PostAuthRedirector pick up).
  useEffect(() => {
    if (!awaitingSoftDeletedCheck) return;
    if (softDeletedInfo) {
      setStep("soft-deleted");
      setAwaitingSoftDeletedCheck(false);
      // #3097 — release the verify spinner now that the modal has
      // transitioned to its next visible state (the restore step).
      setLoading(false);
      return;
    }
    // Normal sign-in: close the instant the ensure-user round-trip resolves
    // with a profile rather than holding the spinner for the full fallback
    // window below. `profile` is set from the SAME /auth/ensure-user response
    // that would have populated `softDeletedInfo`, so by the time it is
    // non-null the soft-deleted branch above has already had its chance to
    // fire — closing here cannot skip the restore step. Without this the
    // overlay spinner lingered for ~5s while AuthProvider's SIGNED_IN handler
    // had already rendered the authenticated app behind it (the pages
    // "flashing" behind a stuck spinner). ProfileCompletionWatcher /
    // PostAuthRedirector (mounted at provider scope) take over from here for
    // incomplete profiles and post-auth navigation.
    if (profile) {
      setAwaitingSoftDeletedCheck(false);
      onClose();
      return;
    }
    const t = setTimeout(() => {
      setAwaitingSoftDeletedCheck(false);
      onClose();
    }, 5000);
    return () => clearTimeout(t);
  }, [awaitingSoftDeletedCheck, softDeletedInfo, profile, onClose]);

  const submitRestore = useCallback(async () => {
    setRestoreError(null);
    setRestoreBusy(true);
    const { error: restoreErr } = await restoreAccount();
    setRestoreBusy(false);
    if (restoreErr) {
      setRestoreError(restoreErr.message || "Couldn't restore your account. Please try again.");
      return;
    }
    onClose();
  }, [restoreAccount, onClose]);

  const cancelRestore = useCallback(async () => {
    try { await signOut(); } catch { /* noop */ }
    onClose();
  }, [signOut, onClose]);

  // Explicit "Send a new code" (Task #5603). Forces a fresh OTP (bypassing the
  // accidental-duplicate dedupe), then holds a short cooldown so rapid taps
  // can't mint superseding codes. When the OTP send is rate-limited (Supabase
  // or our proxy), we automatically fall back to the admin-minted sign-in link
  // delivered via Resend — which bypasses the GoTrue send interval entirely —
  // so the recovery button ALWAYS gets the user a working way in. Returns true
  // when either a code OR a sign-in link was dispatched, so the code screen can
  // show the right confirmation.
  const resendOtpCode = useCallback(async (): Promise<boolean> => {
    if (Date.now() < resendCooldownUntil) return false;
    setBypassLinkSent(false);
    setAlreadySentNotice(false);
    const res = await sendOtp(email, { force: true });
    if (res.ok) {
      setResendCooldownUntil((prev) => Math.max(prev, Date.now() + OTP_RESEND_COOLDOWN_MS));
      return true;
    }
    if (res.rateLimited) {
      try {
        const bypass = await sendMagicLinkBypass(email, next || undefined);
        if (bypass.ok) {
          setBypassLinkSent(true);
          setOtpRateLimited(false);
          setError(null);
          setResendCooldownUntil(Date.now() + OTP_RESEND_COOLDOWN_MS);
          track("auth_otp_fallback_link_sent", { surface: "modal" });
          track("auth_otp_fallback_link_recovery", otpJourneyRef.current);
          return true;
        }
      } catch {
        // Fall through — the rate-limit error set by sendOtp still stands.
      }
    }
    return false;
  }, [email, sendOtp, resendCooldownUntil]);

  const submitPassword = useCallback(async () => {
    setError(null);
    // A pending reset cooldown notice must never gate or linger over a
    // password attempt — the two surfaces are independent.
    setResetError(null);
    setLoading(true);
    const { error: signInError } = await signInWithEmail(email, password);
    setLoading(false);
    if (signInError) {
      // Server-enforced cooldown (per-email/per-IP failure cap or
      // exponential backoff). Surface the exact seconds-until-retry so
      // the user knows when they can try again. This must take
      // precedence over the generic "invalid credentials" collapse
      // below, otherwise a brute-force-defense 429 would render as a
      // misleading "wrong password" message.
      const retryAfter = rateLimitRetryAfter(signInError as { code?: string; retryAfter?: number });
      if (retryAfter !== null) {
        trackCheckoutAuth("failed", "password", "rate_limited");
        setError(
          t("auth.rate_limited_check", {
            defaultValue: "Too many attempts. Please wait {{time}} before trying again.",
            time: formatRetryAfterDuration(retryAfter),
          }),
        );
        return;
      }
      // Special-case ONLY the "email not confirmed" path — this is the
      // post-signup user who genuinely needs the resend-verification
      // flow. Every other Supabase failure (wrong password, missing
      // account, MFA, throttle) collapses to a uniform "email or
      // password is incorrect" message so the bounce is the same shape
      // for an existing-but-wrong-password user as for an unknown
      // email (task #1605). The unconfirmed branch is safe to surface
      // because the user already proved possession of the inbox by
      // signing up — it's not an enumeration vector.
      //
      // Task #3890 (S-M7) — branch on the server's stable `email_not_confirmed`
      // machine code, NOT on parsed error wording: the server no longer leaks
      // Supabase's raw "Email not confirmed" string (which was an enumeration
      // oracle). Fall back to the legacy message check only for resilience.
      const code = (signInError as { code?: string }).code;
      const msg = (signInError.message || "").toLowerCase();
      if (code === "email_not_confirmed" || msg.includes("not confirmed") || msg.includes("email not confirmed")) {
        setStep("verify-pending");
        return;
      }
      // The server accepted the credentials but a local auth-client step
      // (setSession/getSession) stalled past its timeout — surface a plain
      // retryable message instead of the misleading "wrong password" copy.
      if (code === "auth_client_timeout") {
        trackCheckoutAuth("failed", "password", "client_timeout");
        setError(
          t("auth.client_timeout", {
            defaultValue:
              "We signed you in, but the session didn't finish setting up. Please try again.",
          }),
        );
        return;
      }
      setError(
        t("auth.invalid_credentials", {
          defaultValue:
            "We couldn't sign you in with that email and password. If you're new here, continue with email below and we'll send you a one-time code to create your account. No password needed.",
        }),
      );
      trackCheckoutAuth("failed", "password", "invalid_credentials");
      return;
    }
    // Same shape as submitOtpCode: AuthProvider drives the profile
    // refetch off SIGNED_IN, ProfileCompletionWatcher renders the
    // completion dialog if needed, and PostAuthRedirector navigates to
    // `next` only once the freshly-loaded profile is complete.
    rememberPostAuthNext(next);
    // Task #2451 — close immediately so a normal password sign-in
    // doesn't sit on a 5s watcher. If the freshly-signed-in user is
    // actually inside the GDPR soft-deleted window, AuthProvider's
    // /auth/ensure-user response sets `softDeletedInfo`, and the
    // global SoftDeletedAutoOpener re-opens the modal directly on
    // the restore step. This gives soft-deleted users the same
    // restore UX without delaying happy-path sign-ins.
    onClose();
  }, [email, password, signInWithEmail, t, next, onClose, trackCheckoutAuth]);

  const submitSignup = useCallback(async () => {
    setError(null);
    setDuplicateAccount(false);
    if (!firstName.trim() || !lastName.trim()) {
      setError(t("auth.name_required") || "Please enter your first and last name");
      return;
    }
    if (password.length < 6) {
      setError(t("auth.password_min"));
      return;
    }
    if (!acceptTerms) {
      setError(t("auth.accept_terms"));
      return;
    }
    setLoading(true);
    // Task #1605 — the previous lookup-based duplicate preflight has been
    // removed. /auth/lookup now returns a uniform response that no longer
    // distinguishes existing accounts, so the preflight would block every
    // signup. Duplicate detection happens downstream in the
    // signUpWithEmail provider call (Supabase returns a user with an
    // empty `identities[]` array for an existing email), which the
    // signup-error handling already maps to setDuplicateAccount.
    // Stash the user's terms/marketing choices so we can record them on the
    // very first authenticated ensure-user call — this works for both
    // immediate-session signups AND email-confirmation flows where the
    // session only arrives later (AuthProvider drains this on first call).
    try {
      localStorage.setItem(
        "auth_pending_signup_meta",
        JSON.stringify({
          marketing_opt_in: !marketingOptOut,
          accept_terms: true,
          ts: Date.now(),
        }),
      );
    } catch { /* noop */ }
    const { error: signUpError, needsConfirmation } = await signUpWithEmail(
      email,
      password,
      {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      },
      next || undefined,
    );
    if (signUpError) {
      setLoading(false);
      const dup = (signUpError as Error & { duplicate?: boolean }).duplicate;
      if (dup) {
        setDuplicateAccount(true);
        setError(t("auth.account_exists") || "An account with this email already exists.");
      } else {
        setError(signUpError.message || t("auth.sign_up_failed"));
      }
      return;
    }

    // If a session was created immediately, eagerly forward the pending
    // metadata so the very next profile load reflects it.
    try {
      const { getSupabaseClient } = await import("@/lib/supabase/client");
      const sb = getSupabaseClient();
      const { data: { session } } = sb ? await sb.auth.getSession() : { data: { session: null } };
      if (session?.access_token) {
        await fetch(`${apiBase}api/auth/ensure-user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            platform_origin: "bac",
            marketing_opt_in: !marketingOptOut,
            accept_terms: true,
          }),
        });
        try { sessionStorage.removeItem("ensure_user_cache"); } catch {}
        try { localStorage.removeItem("auth_pending_signup_meta"); } catch {}
      }
    } catch {
      // ignore — AuthProvider will drain the pending metadata on the first
      // authenticated ensure-user call after email confirmation.
    }

    setLoading(false);
    if (needsConfirmation) {
      setStep("signup-confirm");
    } else {
      // PostAuthRedirector handles the deferred-redirect case once
      // is_complete flips true; ProfileCompletionWatcher catches the
      // brand-new-user path. No setTimeout polling.
      rememberPostAuthNext(next);
      onClose();
    }
  }, [firstName, lastName, password, acceptTerms, email, marketingOptOut, signUpWithEmail, t, onClose, next, apiBase]);

  const handleForgot = useCallback(async () => {
    // Reset feedback goes to its OWN `resetError` state, NEVER the shared
    // `error` banner. The reset email-send limiter has a 1-hour window, so
    // its cooldown retry-after can be tens of minutes; surfacing that on the
    // password screen made password login look locked for ~an hour when it
    // wasn't rate-limited at all. We also clear any stale login `error` so
    // the password banner doesn't sit alongside the reset notice.
    setError(null);
    setResetError(null);
    // Bump a per-click counter so EVERY "Forgot password?" press reads as a
    // fresh acknowledgement — even when the resulting message is byte-identical
    // (e.g. the same cooldown on a rapid second click). The notice is keyed on
    // this counter so React remounts it, re-announcing it to assistive tech and
    // replaying its flash animation instead of silently no-op'ing.
    setResetAttempt((n) => n + 1);
    // Dedicated busy flag (NOT the shared `loading`) so the Forgot action gets
    // its own in-flight state without putting the password-login submit button
    // into a spinner.
    setResetBusy(true);
    const { error: resetErr } = await resetPassword(email);
    setResetBusy(false);
    if (resetErr) {
      // Server-enforced reset cooldown — format the retry-after as a
      // reset-scoped message instead of leaking the raw "wait 3511 seconds"
      // string onto a surface it doesn't apply to.
      const retryAfter = rateLimitRetryAfter(resetErr as { code?: string; retryAfter?: number });
      if (retryAfter !== null) {
        setResetError(
          t("auth.rate_limited_reset", {
            defaultValue: "Too many reset requests. Please wait {{time}} before trying again.",
            time: formatRetryAfterDuration(retryAfter),
          }),
        );
        return;
      }
      if ((resetErr as { code?: string }).code === "transport") {
        setResetError(
          t("auth.transient_send_failure", {
            defaultValue: "We couldn't reach the server. Please try again in a moment.",
          }),
        );
        return;
      }
      if ((resetErr as { code?: string }).code === "unavailable") {
        // Task #6388 — the server's rate-limit store is temporarily down and
        // the reset limiter couldn't be evaluated. NOT a real rate limit, so
        // never render cooldown copy; reuse the existing (fully translated)
        // "couldn't send right now — try again" notice, which is exactly what
        // the user should do once the store self-heals moments later.
        setResetError(
          t("auth.reset_send_failed", {
            defaultValue: "We couldn't send the reset email right now. Please try again.",
          }),
        );
        return;
      }
      if ((resetErr as { code?: string }).code === "send_failed") {
        // Task #4984 — the server reached Supabase but the recovery email
        // genuinely could not be sent (config/provider error). Tell the user
        // honestly instead of advancing to "check your email" for a message
        // that will never arrive.
        setResetError(
          t("auth.reset_send_failed", {
            defaultValue: "We couldn't send the reset email right now. Please try again.",
          }),
        );
        return;
      }
      setResetError(resetErr.message);
      return;
    }
    setStep("magic-sent");
  }, [email, resetPassword, t]);

  const handleSocial = useCallback(
    async (provider: SocialProvider) => {
      // Task #4226 — Facebook sign-in is retired. Rather than starting
      // Facebook OAuth, route to the on-brand retirement view that points
      // the user at the email tied to their account. Google/Apple unchanged.
      if (provider === "facebook") {
        setError(null);
        setLoading(false);
        setStep("facebook-retired");
        return;
      }
      setError(null);
      setLoading(true);
      // Preserve the browser's booking destination before leaving for OAuth.
      // The callback owns this value until its session/profile work completes.
      rememberPostAuthNext(next);
      const { error: oauthErr } = await signInWithOAuth(provider, next || undefined);
      if (oauthErr) {
        rememberPostAuthNext(null);
        setLoading(false);
        setError(oauthErr.message || t("auth.oauth_failed"));
        trackCheckoutAuth("failed", "oauth", "provider_error");
      }
      // OAuth navigates away; no need to close.
    },
    [signInWithOAuth, next, t, trackCheckoutAuth],
  );

  const headerTitle = (() => {
    switch (step) {
      case "email":
        if (intent === "signup") return t("auth.create_account");
        if (contextHint === "list-a-car") {
          return t("auth.log_in_or_sign_up_to_list", { defaultValue: "Log in or sign up to list your car" });
        }
        if (contextHint === "reserve-car") {
          return t("auth.log_in_or_sign_up_to_reserve", { defaultValue: "Log in or sign up to reserve this car" });
        }
        return t("auth.log_in_or_sign_up");
      case "code":
        return t("auth.confirm_its_you", { defaultValue: "Confirm it's you" });
      case "password":
        return t("auth.continue_with_password", { defaultValue: "Continue with password" });
      case "social-hint":
        return t("auth.continue_with_email", { defaultValue: "Continue with email" });
      case "signup":
        return t("auth.create_account");
      case "magic-sent":
        return t("auth.check_your_email");
      case "signup-confirm":
        return t("auth.check_your_email");
      case "verify-pending":
        return t("auth.confirm_your_email");
      case "soft-deleted":
        return t("auth.account_previously_deleted", { defaultValue: "Welcome back" });
      case "facebook-retired":
        return t("auth.facebook_retired_title", { defaultValue: "Facebook sign-in retired" });
      default:
        return t("auth.log_in_or_sign_up");
    }
  })();

  const showBack = step !== "email" && step !== "magic-sent" && step !== "signup-confirm" && step !== "soft-deleted";

  return (
    <ModalShell
      open={isOpen}
      onClose={onCancel}
      accessibleTitle={headerTitle}
      accessibleDescription={t("auth.dialog_description", {
        defaultValue: "Sign in or create an account to continue.",
      })}
      showBack={showBack}
      onBack={goBackToEmail}
      size="xl"
      hideHeaderDivider
      contentClassName="sm:max-w-[568px] sm:rounded-3xl"
      // Bottom padding intentionally exceeds top: ModalShell's header
      // (X button + its py-4) already adds ~68px above the body on
      // desktop, so a matching pt would push content too far down.
      // We pad the bottom to ~match the *total* top whitespace
      // (header strip + body pt) so the card looks visually balanced.
      bodyClassName="px-6 pb-10 pt-1 sm:px-10 sm:pb-14 sm:pt-2"
      data-testid="auth-modal"
    >
      <div className="mb-7 flex flex-col items-center text-center">
        <button
          type="button"
          onClick={goHomeFromLogo}
          aria-label={t("auth.go_to_home_page")}
          className="mb-5 inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
        >
          <Logo size="md" />
        </button>
        <h1 className="text-[22px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[26px]">
          {headerTitle}
        </h1>
        {step === "email" && contextHint === "reserve-car" && (
          <p className="mt-2 max-w-md text-sm leading-5 text-gray-600">
            {t("auth.reserve_sign_in_explainer", {
              defaultValue: "Your selections are saved. After signing in, you’ll continue to checkout to reserve this car.",
            })}
          </p>
        )}
      </div>
      {step === "email" && (
        <EmailStep
          email={email}
          onChange={(v) => {
            // Any edit invalidates the rate-limit context — the user may be
            // switching to a different email that isn't cooled-down.
            if (otpRateLimited) setOtpRateLimited(false);
            if (magicLinkBypassSent) setMagicLinkBypassSent(false);
            // Task #6917 — dismiss the session-expired notice the instant the
            // user starts editing so it doesn't linger over the sign-in flow.
            if (sessionExpiredBanner) setSessionExpiredBanner(false);
            setEmail(v);
          }}
          onSubmit={submitEmailStep}
          onSocial={handleSocial}
          loading={loading}
          error={error}
          inputRef={emailInputRef}
          rateLimited={otpRateLimited}
          onUsePassword={() => {
            setError(null);
            setOtpRateLimited(false);
            setStep("password");
          }}
          onSendMagicLink={requestBypassMagicLink}
          magicLinkSent={magicLinkBypassSent}
          sendingMagicLink={sendingBypassLink}
          rememberMe={rememberMe}
          onRememberMeChange={(v) => { setRememberMe(v); setRememberMePreference(v); }}
          sessionExpiredNotice={sessionExpiredBanner}
          onDismissSessionExpiredNotice={() => setSessionExpiredBanner(false)}
        />
      )}
      {step === "code" && (
        <CodeStep
          email={email}
          code={otpCode}
          onCodeChange={setOtpCode}
          onSubmit={submitOtpCode}
          onResend={resendOtpCode}
          onUsePassword={switchToPassword}
          loading={loading}
          sending={sendingCode}
          error={error}
          cooldownUntil={resendCooldownUntil}
          bypassLinkSent={bypassLinkSent}
          alreadySent={alreadySentNotice}
          sendConfirmed={otpSendConfirmed}
        />
      )}
      {step === "password" && (
        <PasswordStep
          email={email}
          password={password}
          showPassword={showPassword}
          onPasswordChange={setPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
          onSubmit={submitPassword}
          onForgot={handleForgot}
          onUseCode={() => {
            setError(null); setResetError(null);
            // A direct password entry has not necessarily requested a code.
            if (initialStep === "password" && !otpSendConfirmed) submitEmailStep();
            else setStep("code");
          }}
          loading={loading}
          error={error}
          resetError={resetError}
          resetBusy={resetBusy}
          resetAttempt={resetAttempt}
        />
      )}
      {step === "social-hint" && (
        <SocialHintStep
          email={email}
          providers={lookup?.providers || []}
          onSocial={handleSocial}
          onContinueEmail={goBackToEmail}
          loading={loading}
        />
      )}
      {step === "signup" && (
        <SignupStep
          email={email}
          firstName={firstName}
          lastName={lastName}
          password={password}
          marketingOptOut={marketingOptOut}
          acceptTerms={acceptTerms}
          onFirstNameChange={setFirstName}
          onLastNameChange={setLastName}
          onPasswordChange={setPassword}
          onMarketingChange={setMarketingOptOut}
          onTermsChange={setAcceptTerms}
          onSubmit={submitSignup}
          onSocial={handleSocial}
          loading={loading}
          error={error}
          duplicateAccount={duplicateAccount}
          onSwitchToLogin={switchToLogin}
        />
      )}
      {(step === "magic-sent" || step === "signup-confirm") && (
        <SentStep email={email} variant={step} apiBase={apiBase} />
      )}
      {step === "verify-pending" && (
        <VerifyPendingStep email={email} apiBase={apiBase} />
      )}
      {step === "soft-deleted" && softDeletedInfo && (
        <SoftDeletedStep
          email={email}
          deletedAt={softDeletedInfo.deletedAt}
          scheduledErasureAt={softDeletedInfo.scheduledErasureAt}
          onRestore={submitRestore}
          onCancel={cancelRestore}
          loading={restoreBusy}
          error={restoreError}
        />
      )}
      {step === "facebook-retired" && (
        <FacebookRetiredStep email={email} onContinueEmail={goBackToEmail} />
      )}
    </ModalShell>
  );
}

function EmailStep({
  email,
  onChange,
  onSubmit,
  onSocial,
  loading,
  error,
  inputRef,
  rateLimited,
  onUsePassword,
  onSendMagicLink,
  magicLinkSent,
  rememberMe,
  onRememberMeChange,
  sendingMagicLink,
  sessionExpiredNotice,
  onDismissSessionExpiredNotice,
}: {
  email: string;
  onChange: (e: string) => void;
  onSubmit: () => void;
  onSocial: (p: SocialProvider) => void;
  loading: boolean;
  error: string | null;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  // When true, Supabase blocked the OTP send because of its server-side
  // rate limit. We render a "Sign in with your password instead" escape
  // hatch underneath the error so password-having users aren't trapped
  // behind the cooldown window. The link is suppressed in the normal
  // (non-rate-limited) flow so the email step stays Airbnb-style simple.
  rateLimited?: boolean;
  onUsePassword?: () => void;
  // Bypass: when rate-limited, we offer a server-minted sign-in link
  // delivered via Resend (POST /auth/send-magic-link). After a successful
  // send, `magicLinkSent` is true and we render a "check your email"
  // confirmation in place of the buttons.
  onSendMagicLink?: () => void;
  magicLinkSent?: boolean;
  sendingMagicLink?: boolean;
  // Task #3061 — Remember-me choice flows through here so the Supabase
  // client's custom storage adapter can route refresh tokens to either
  // localStorage (remember) or sessionStorage (one-shot session).
  rememberMe: boolean;
  onRememberMeChange: (value: boolean) => void;
  // Task #6917 — when true, a "your session has expired" notice is shown
  // above the email field. Dismissed as soon as the user starts typing.
  sessionExpiredNotice?: boolean;
  onDismissSessionExpiredNotice?: () => void;
}) {
  const { t } = useTranslation();
  // Inline email validation: only show the inline error after the user has
  // either left the field (blur) or attempted to submit. Until then the
  // field looks neutral so we don't yell at people mid-typing. The submit
  // button stays disabled while the value is empty *or* invalid so we never
  // burn a Supabase OTP send on a typo like `ben@gti.im\.` (#dstest1).
  const [touched, setTouched] = useState(false);
  const trimmed = email.trim();
  const emailValid = isValidEmail(trimmed.toLowerCase());
  const showInlineEmailError = touched && trimmed.length > 0 && !emailValid;
  const inlineErrorText = showInlineEmailError
    ? t("auth.invalid_email") || "Please enter a valid email"
    : error;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        // Task #6917 — dismiss the session-expired notice on submit.
        if (sessionExpiredNotice) onDismissSessionExpiredNotice?.();
        onSubmit();
      }}
      className="space-y-4"
    >
      {sessionExpiredNotice && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800 text-sm">
            {t("auth.session_expired_notice", {
              defaultValue: "Your session has expired. Please sign in again.",
            })}
          </AlertDescription>
        </Alert>
      )}
      <FloatingField
        label={t("auth.email_placeholder") || "Email address"}
        type="email"
        value={email}
        onChange={(v) => {
          // Strip whitespace as the user types — common mobile-keyboard
          // mistake is a leading or trailing space from autocomplete.
          onChange(v.replace(/\s+/g, ""));
          // Once the user starts typing again, hide the inline error
          // until they next blur or submit. Otherwise every keystroke
          // re-evaluates after the first blur and the field keeps
          // yelling "invalid email" while they're still mid-typing.
          if (touched) setTouched(false);
        }}
        onBlur={() => setTouched(true)}
        autoFocus
        autoComplete="email"
        required
        inputRef={inputRef}
        invalid={showInlineEmailError}
      />
      {inlineErrorText && <p className="text-sm font-medium text-red-600">{inlineErrorText}</p>}
      {rateLimited && magicLinkSent ? (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
          {t("auth.magic_link_sent_check_email", {
            defaultValue:
              "Codes weren't going through, so we emailed you a secure sign-in link instead. Open the newest email and tap the button to sign in. No code needed.",
          })}
        </p>
      ) : rateLimited && (onSendMagicLink || onUsePassword) ? (
        <div className="flex flex-col gap-2">
          {onSendMagicLink && (
            <button
              type="button"
              onClick={onSendMagicLink}
              disabled={!emailValid || sendingMagicLink}
              className="text-left text-sm font-medium text-brand underline underline-offset-2 hover:text-brand-hover disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
            >
              {sendingMagicLink
                ? (t("common.sending") || "Sending...")
                : t("auth.email_me_sign_in_link")}
            </button>
          )}
          {onUsePassword && (
            <button
              type="button"
              onClick={onUsePassword}
              className="text-left text-sm font-medium text-brand underline underline-offset-2 hover:text-brand-hover"
            >
              {t("auth.use_password_instead")}
            </button>
          )}
        </div>
      ) : null}
      <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => onRememberMeChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 accent-brand text-brand focus:ring-brand"
        />
        {t("auth.remember_me", { defaultValue: "Remember me on this device" })}
      </label>
      <p className="text-xs leading-snug text-gray-600">
        {t("auth.email_terms_blurb", { defaultValue: "We'll email you a confirmation code." })}{" "}
        <Link href="/privacy" className="font-medium text-gray-900 underline underline-offset-2">
          {t("auth.privacy_policy")}
        </Link>
      </p>
      <button
        type="submit"
        disabled={loading || !emailValid}
        className="primary-action flex h-12 w-full items-center justify-center rounded-xl px-4 text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("auth.continue_btn")}
      </button>

      <Divider label={t("auth.or") || "or"} />
      <SocialButtons onSocial={onSocial} disabled={loading} />
    </form>
  );
}

function CodeStep({
  email,
  code,
  onCodeChange,
  onSubmit,
  onResend,
  onUsePassword,
  loading,
  sending,
  error,
  cooldownUntil,
  bypassLinkSent,
  alreadySent,
  sendConfirmed,
}: {
  email: string;
  code: string;
  onCodeChange: (c: string) => void;
  onSubmit: () => void;
  onResend: () => Promise<boolean>;
  onUsePassword: () => void;
  loading: boolean;
  sending: boolean;
  error: string | null;
  // Timestamp (ms) until which the explicit resend is held; surfaced as a
  // live countdown so users know when they can request another code.
  cooldownUntil?: number;
  // True when the recovery path fell back to an admin-minted sign-in link
  // (Resend) because the OTP send was rate-limited (Task #5603).
  bypassLinkSent?: boolean;
  // Task #6311 — true when the server deduped a repeat request because a
  // still-valid code went out moments ago (no new email was sent).
  alreadySent?: boolean;
  // A successful response or already-sent dedupe confirmed an OTP is in flight.
  sendConfirmed?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const submittedForCodeRef = useRef<string>("");
  const [resentAt, setResentAt] = useState<number | null>(null);
  // 1s tick so the resend cooldown counts down live without a manual refresh.
  const [now, setNow] = useState(() => Date.now());
  const cooldownRemainingMs = Math.max(0, (cooldownUntil ?? 0) - now);
  const cooldownSeconds = Math.ceil(cooldownRemainingMs / 1000);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (cooldownRemainingMs <= 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownRemainingMs]);

  // Reset the auto-submit guard whenever the user edits below 6 digits
  // (e.g. after a wrong-code error so they can retry by editing one digit).
  useEffect(() => {
    const cleaned = code.replace(/\D/g, "");
    if (cleaned.length < 6) {
      submittedForCodeRef.current = "";
      return;
    }
    // Auto-submit exactly once per fully-entered code.
    if (cleaned.length === 6 && !loading && submittedForCodeRef.current !== cleaned) {
      submittedForCodeRef.current = cleaned;
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleResend = async () => {
    setResentAt(null);
    const ok = await onResend();
    if (ok) setResentAt(Date.now());
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-5"
    >
      {(sending || sendConfirmed) && (
        <p className="-mt-3 text-center text-base text-gray-600">
          {sending
            ? t("auth.otp_sending", { defaultValue: "Sending your code to {{email}}…", email })
            : t("auth.otp_sent_to", { defaultValue: "We sent a code to {{email}}.", email })}
        </p>
      )}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="– – – – – –"
        aria-label={t("auth.otp_label", { defaultValue: "6-digit code" })}
        // #3097 — keep the field non-editable while verify/post-verify
        // is in flight so users can't retype mid-sign-in. readOnly (not
        // disabled) preserves the existing visual style and keeps the
        // one-time-code autofill target intact for password managers.
        readOnly={loading}
        aria-busy={loading}
        className="block w-full rounded-2xl border border-gray-400 bg-white px-6 py-5 text-center font-mono text-3xl tracking-[0.45em] text-gray-900 transition-colors placeholder:text-gray-300 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand read-only:cursor-not-allowed read-only:opacity-70"
      />
      {error && (
        <LiveStatus politeness="assertive" className="text-center text-sm font-medium text-red-600">
          {error}
        </LiveStatus>
      )}
      {(loading || sending) && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          {sending && !loading && (
            <span>{t("auth.otp_sending_short", { defaultValue: "Sending your code…" })}</span>
          )}
        </div>
      )}
      <p className="text-center text-xs text-gray-500">
        {t("auth.otp_use_latest", { defaultValue: "Always use the code from the most recent email." })}
      </p>
      <p className="text-center text-base text-gray-700">
        {t("auth.otp_didnt_get", { defaultValue: "Didn't get it?" })}{" "}
        <button
          type="button"
          onClick={handleResend}
          disabled={loading || sending || cooldownSeconds > 0}
          className="font-semibold text-gray-900 underline underline-offset-4 disabled:no-underline disabled:opacity-50"
        >
          {cooldownSeconds > 0
            ? t("auth.otp_resend_in", { defaultValue: "Send a new code in {{seconds}}s", seconds: cooldownSeconds })
            : t("auth.otp_resend", { defaultValue: "Send a new code" })}
        </button>
      </p>
      {alreadySent && !bypassLinkSent && !resentAt && (
        <LiveStatus politeness="polite" className="text-center text-xs text-gray-600">
          {t("auth.otp_already_sent", {
            defaultValue:
              "A code was already sent to your email just now. It can take a minute to arrive. Check your spam folder, and use the code from the most recent email.",
          })}
        </LiveStatus>
      )}
      {bypassLinkSent ? (
        <p className="text-center text-xs text-emerald-700">
          {t("auth.otp_magic_link_sent", {
            defaultValue:
              "Codes weren't going through, so we've emailed you a secure sign-in link instead. Open the newest email and tap the button. It signs you in directly, with no code needed.",
          })}
        </p>
      ) : resentAt ? (
        <p className="text-center text-xs text-emerald-700">
          {t("auth.otp_resent", { defaultValue: "New code sent." })}
        </p>
      ) : null}
      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={onUsePassword}
          className="text-xs font-medium text-gray-500 underline decoration-gray-300 underline-offset-4 hover:text-gray-900 hover:decoration-gray-900"
        >
          {t("auth.use_password_instead", { defaultValue: "Use password instead" })}
        </button>
      </div>
    </form>
  );
}

function PasswordStep({
  email,
  password,
  showPassword,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
  onForgot,
  onUseCode,
  loading,
  error,
  resetError,
  resetBusy,
  resetAttempt,
}: {
  email: string;
  password: string;
  showPassword: boolean;
  onPasswordChange: (p: string) => void;
  onTogglePassword: () => void;
  onSubmit: () => void;
  onForgot: () => void;
  onUseCode: () => void;
  loading: boolean;
  error: string | null;
  // Password-reset cooldown / feedback, scoped to this surface and rendered
  // separately from the login `error` banner so a long reset email cooldown
  // never reads as a password-login lockout (see handleForgot).
  resetError?: string | null;
  // True while a reset email send is in flight — drives the Forgot button's
  // busy/disabled state so the click is always acknowledged.
  resetBusy?: boolean;
  // Increments on every Forgot click; used to key the notice so a repeated
  // click re-acknowledges even when the message text is identical.
  resetAttempt?: number;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <p className="-mt-3 text-center text-sm text-gray-500">{email}</p>
      <FloatingField
        label={t("auth.password_placeholder") || "Password"}
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={onPasswordChange}
        autoFocus
        autoComplete="current-password"
        required
        suffix={
          <button
            type="button"
            onClick={onTogglePassword}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="cursor-pointer inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        }
      />
      {error && (
        <LiveStatus politeness="assertive" className="text-sm font-medium text-red-600">
          {error}
        </LiveStatus>
      )}
      <button
        type="submit"
        disabled={loading || !password}
        className="primary-action flex h-12 w-full items-center justify-center rounded-xl px-4 text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("auth.log_in_btn")}
      </button>

      <div className="flex flex-col items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onForgot}
          disabled={resetBusy}
          aria-busy={resetBusy}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 underline decoration-gray-300 underline-offset-4 hover:decoration-gray-900 disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
        >
          {resetBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {resetBusy
            ? t("auth.sending_reset", { defaultValue: "Sending reset email…" })
            : t("auth.forgot_password")}
        </button>
        {/* Reset feedback is rendered here, scoped to the Forgot action and
            styled as a prominent-but-calm informational notice (NOT the red
            login error). Keyed on resetAttempt so a repeated click re-mounts
            and re-announces the notice even when the text is unchanged, so the
            action never reads as a dead button. */}
        {resetError && (
          <LiveStatus
            key={resetAttempt}
            politeness="polite"
            data-testid="auth-reset-notice"
            className="w-full max-w-xs rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center text-sm font-medium text-amber-900 motion-safe:animate-in motion-safe:fade-in"
          >
            {resetError}
          </LiveStatus>
        )}
        <button
          type="button"
          onClick={onUseCode}
          className="text-sm font-medium text-gray-900 underline decoration-gray-300 underline-offset-4 hover:decoration-gray-900"
        >
          {t("auth.use_email_code", { defaultValue: "Use email code instead" })}
        </button>
      </div>
    </form>
  );
}

function SocialHintStep({
  email,
  providers,
  onSocial,
  onContinueEmail,
  loading,
}: {
  email: string;
  providers: string[];
  onSocial: (p: SocialProvider) => void;
  onContinueEmail: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const legacySocialEnabled = useFeatureFlag("feature_apple_google_signin");
  const googleEnabled = useFeatureFlag("feature_google_signin") || legacySocialEnabled;
  const appleEnabled = useFeatureFlag("feature_apple_signin") || legacySocialEnabled;
  const social = providers
    .filter(isSocialProvider)
    .filter((p) =>
      p === "google" ? googleEnabled :
      p === "apple" ? appleEnabled :
      true,
    );
  // Task #4226 — Facebook is retired. Drop any Facebook button from the
  // social-hint screen; never render a dead "Continue with Facebook".
  const workingSocial = social.filter((p) => p !== "facebook");
  const hasFacebook = social.includes("facebook");

  // Returning Facebook-ONLY accounts: there's no working social option, so
  // show the full retirement guidance and route them to the email path
  // (their email is already known here, so it'll be pre-filled).
  if (hasFacebook && workingSocial.length === 0) {
    return <FacebookRetiredView email={email} onContinueEmail={onContinueEmail} />;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 sm:text-base">
        {t("auth.social_hint_desc", { email })}
      </p>
      <div className="space-y-2">
        {workingSocial.map((p) => (
          <SocialButton key={p} provider={p} onClick={() => onSocial(p)} disabled={loading} />
        ))}
      </div>
      {hasFacebook && (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-center text-xs text-gray-600">
          {t("auth.facebook_retired_hint", {
            defaultValue: "Facebook sign-in has been retired. Use your email or another option above instead.",
          })}
        </p>
      )}
      <p className="text-center text-xs text-gray-500">
        {t("auth.social_hint_mismatch")}
      </p>
    </div>
  );
}

/**
 * Task #4226 — On-brand "Facebook login retired" view.
 *
 * Rendered both as a standalone step (when the Facebook button is clicked
 * from the email / sign-up screens) and inline on the social-hint screen
 * for accounts that only ever linked Facebook. Shows the recognizable
 * Facebook mark, explains the retirement, routes the user to the email
 * sign-in step (their email is pre-filled when known), and offers a
 * support-chat escape hatch wired to the shared useSupport hook.
 */
function FacebookRetiredView({
  email,
  onContinueEmail,
}: {
  email: string;
  onContinueEmail: () => void;
}) {
  const { t } = useTranslation();
  const { openChat } = useSupport();
  const onContactSupport = useCallback(() => {
    openChat({
      emailOverride: email || undefined,
      context: { reason: "facebook_login_retired", email: email || undefined },
      eventName: "facebook_login_retired_support",
    });
  }, [openChat, email]);

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#1877F2]/10 text-[#1877F2] sm:h-20 sm:w-20">
        <DsIcon name="facebook" className="h-9 w-9 sm:h-10 sm:w-10" />
      </div>
      <p className="max-w-sm text-sm text-gray-600 sm:text-[15px]">
        {t("auth.facebook_retired_body", {
          defaultValue:
            "We've turned off signing in with Facebook. You can still get into your account using the email address it's linked to. Enter your email and we'll send a one-time code, or reset your password.",
        })}
      </p>
      {email && (
        <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          <Mail className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={1.8} />
          <span className="truncate">{email}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onContinueEmail}
        data-testid="auth-facebook-retired-continue-email"
        className="primary-action mt-6 inline-flex h-12 w-full max-w-xs items-center justify-center rounded-xl px-4 text-base font-semibold transition-colors"
      >
        {t("auth.facebook_retired_continue_email", { defaultValue: "Continue with email" })}
      </button>
      <button
        type="button"
        onClick={onContactSupport}
        data-testid="auth-facebook-retired-support"
        className="mt-3 inline-flex h-11 w-full max-w-xs items-center justify-center rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-900 transition hover:bg-gray-50"
      >
        {t("auth.facebook_retired_contact_support", { defaultValue: "Contact support" })}
      </button>
    </div>
  );
}

function FacebookRetiredStep({
  email,
  onContinueEmail,
}: {
  email: string;
  onContinueEmail: () => void;
}) {
  return <FacebookRetiredView email={email} onContinueEmail={onContinueEmail} />;
}

export function SignupStep({
  email,
  firstName,
  lastName,
  password,
  marketingOptOut,
  acceptTerms,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onMarketingChange,
  onTermsChange,
  onSubmit,
  onSocial,
  loading,
  error,
  duplicateAccount,
  onSwitchToLogin,
}: {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  marketingOptOut: boolean;
  acceptTerms: boolean;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onMarketingChange: (v: boolean) => void;
  onTermsChange: (v: boolean) => void;
  onSubmit: () => void;
  onSocial: (p: SocialProvider) => void;
  loading: boolean;
  error: string | null;
  duplicateAccount?: boolean;
  onSwitchToLogin?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t("auth.first_name")}</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            required
            autoFocus
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-base transition-shadow placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t("auth.last_name")}</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            required
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-base transition-shadow placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{t("auth.password")}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder={t("auth.password_min") || "At least 6 characters"}
          required
          minLength={6}
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-base transition-shadow placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
        {error && (
          <LiveStatus politeness="assertive" as="div" className="space-y-1">
            <p className="text-sm font-medium text-red-600">{error}</p>
            {duplicateAccount && onSwitchToLogin && (
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-sm font-semibold text-gray-900 underline decoration-gray-300 underline-offset-4 hover:decoration-gray-900"
              >
                {t("auth.account_exists_log_in")}
              </button>
            )}
          </LiveStatus>
        )}
      </div>

      <label className="flex items-start gap-2 text-xs text-gray-700">
        <input
          type="checkbox"
          checked={marketingOptOut}
          onChange={(e) => onMarketingChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
        />
        <span>{t("auth.marketing_opt_out")}</span>
      </label>
      <label className="flex items-start gap-2 text-xs text-gray-700">
        <input
          type="checkbox"
          checked={acceptTerms}
          onChange={(e) => onTermsChange(e.target.checked)}
          required
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
        />
        <span>
          {t("auth.terms_agree")}{" "}
          <Link
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t("auth.terms_of_service")} (${t("auth.opens_in_new_tab")})`}
            className="underline"
          >
            {t("auth.terms_of_service")}
          </Link>{" "}
          {t("auth.and")}{" "}
          <Link
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t("auth.privacy_policy")} (${t("auth.opens_in_new_tab")})`}
            className="underline"
          >
            {t("auth.privacy_policy")}
          </Link>
          .
        </span>
      </label>
      <button
        type="submit"
        disabled={loading || !firstName.trim() || !lastName.trim() || password.length < 6 || !acceptTerms}
        className="primary-action flex h-12 w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("auth.create_account_btn")}
      </button>
      <Divider label={t("auth.or") || "or"} />
      <SocialButtons onSocial={onSocial} disabled={loading} />
    </form>
  );
}

function SentStep({ email, variant, apiBase }: { email: string; variant: "magic-sent" | "signup-confirm"; apiBase: string }) {
  const { t } = useTranslation();
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const onResend = useCallback(async () => {
    if (resending) return;
    setResendError(null);
    setResending(true);
    try {
      // Both variants need the same kind of email re-issued. The
      // `resend-verification-public` endpoint is a no-op for signed-in users
      // and does the right rate-limited resend for both magic-link and
      // signup-confirm flows.
      const res = await fetch(`${apiBase}api/auth/resend-verification-public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        setResendError(t("auth.too_many_attempts") || "Too many attempts. Please try again later.");
        return;
      }
      if (!res.ok) {
        setResendError(t("auth.resend_failed") || "Couldn't resend right now. Please try again.");
        return;
      }
      setResentAt(Date.now());
    } catch {
      setResendError(t("auth.resend_failed") || "Couldn't resend right now. Please try again.");
    } finally {
      setResending(false);
    }
  }, [apiBase, email, resending, t]);

  const heading = t("auth.check_your_email") || "Check your email";
  const description =
    variant === "signup-confirm"
      ? (t("auth.confirmation_sent_short") || "We sent a confirmation link so you can finish creating your account.")
      : (t("auth.magic_link_sent_short") || "We sent you a one-time sign-in link. Open it to come right back in.");

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 sm:h-20 sm:w-20">
        <Mail className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={1.6} />
      </div>
      <h3 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">{heading}</h3>
      <p className="mt-2 max-w-sm text-sm text-gray-600 sm:text-[15px]">{description}</p>
      <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
        <Mail className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={1.8} />
        <span className="truncate">{email}</span>
      </div>

      {resendError ? (
        <p className="mt-4 text-sm font-medium text-red-600">{resendError}</p>
      ) : null}
      {resentAt ? (
        <p className="mt-4 text-sm font-medium text-emerald-700">
          {t("auth.resend_email_sent")}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 underline decoration-gray-300 underline-offset-4 hover:decoration-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t("auth.resend_email")}
        </button>
        <p className="max-w-xs text-xs text-gray-500">
          {t("auth.verify_pending_hint")}
        </p>
      </div>
    </div>
  );
}

/**
 * Shown when /auth/lookup tells us an account exists in Supabase but the
 * email hasn't been confirmed yet. Lets the user re-trigger the verification
 * email without needing to sign in (which they can't do — Supabase blocks
 * password sign-in for unconfirmed accounts when "Confirm email" is on).
 *
 * The resend endpoint is rate-limited server-side and always returns ok:true
 * regardless of whether the email exists, so we don't need (and shouldn't
 * implement) client-side enumeration protection.
 */
function VerifyPendingStep({ email, apiBase }: { email: string; apiBase: string }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onResend = useCallback(async () => {
    if (sending) return;
    setError(null);
    setSending(true);
    try {
      const res = await fetch(`${apiBase}api/auth/resend-verification-public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        setError(t("auth.too_many_attempts") || "Too many attempts. Please try again later.");
        return;
      }
      if (!res.ok) {
        setError(t("auth.resend_failed") || "Couldn't resend right now. Please try again.");
        return;
      }
      setSentAt(Date.now());
    } catch {
      setError(t("auth.resend_failed") || "Couldn't resend right now. Please try again.");
    } finally {
      setSending(false);
    }
  }, [apiBase, email, sending, t]);

  const heading = t("auth.confirm_your_email") || "Confirm your email";
  const description = t("auth.verify_pending_body_short")
    || "Click the link we just sent to finish setting up your account, then come back here to log in.";

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 sm:h-20 sm:w-20">
        <Mail className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={1.6} />
      </div>
      <h3 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">{heading}</h3>
      <p className="mt-2 max-w-sm text-sm text-gray-600 sm:text-[15px]">{description}</p>
      <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
        <Mail className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={1.8} />
        <span className="truncate">{email}</span>
      </div>

      {error ? (
        <LiveStatus politeness="assertive" className="mt-4 text-sm font-medium text-red-600">
          {error}
        </LiveStatus>
      ) : null}
      {sentAt ? (
        <p className="mt-4 text-sm font-medium text-emerald-700">
          {t("auth.resend_email_sent")}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onResend}
        disabled={sending}
        className="mt-6 inline-flex h-11 w-full max-w-xs items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {t("auth.resend_email_button")}
      </button>
      <p className="mt-3 max-w-xs text-xs text-gray-500">
        {t("auth.verify_pending_hint")}
      </p>
    </div>
  );
}

/**
 * Task #2451 — "Account previously deleted" restore step.
 *
 * Shown immediately after a successful OTP verify when /auth/ensure-user
 * returned `account_status: "soft_deleted"`. The user just proved
 * possession of the inbox so we're allowed to disclose the soft-deleted
 * state; the CTA calls POST /auth/restore-account which clears
 * deleted_at server-side and re-binds the auth principal.
 *
 * Pressing Cancel signs the new Supabase session out (we don't want a
 * tab left holding an authenticated session bound to nothing on the
 * local side).
 */
function SoftDeletedStep({
  email,
  deletedAt,
  scheduledErasureAt,
  onRestore,
  onCancel,
  loading,
  error,
}: {
  email: string;
  deletedAt: string;
  scheduledErasureAt: string;
  onRestore: () => void;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  // Task #2451 — surface the exact dates from the server (deleted on,
  // permanently erased on) so the user knows precisely how long they
  // have to restore. Falls back gracefully if either ISO string fails
  // to parse.
  const fmtDate = (iso: string): string | null => {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return null; }
  };
  const deletedOn = fmtDate(deletedAt);
  const availableUntil = fmtDate(scheduledErasureAt);
  let daysLeft: number | null = null;
  try {
    const diffMs = new Date(scheduledErasureAt).getTime() - Date.now();
    daysLeft = Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
  } catch { /* noop */ }

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-600 sm:h-20 sm:w-20">
        <Mail className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={1.6} />
      </div>
      <h3 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
        {t("auth.account_previously_deleted_heading", { defaultValue: "This account was deleted recently" })}
      </h3>
      <p className="mt-2 max-w-sm text-sm text-gray-600 sm:text-[15px]">
        {t("auth.account_previously_deleted_body", {
          defaultValue:
            "We still have your data on file. Restore your account to keep your past trips, listings, and reviews, or cancel and we'll finish deleting everything on schedule.",
        })}
      </p>
      <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
        <Mail className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={1.8} />
        <span className="truncate">{email}</span>
      </div>
      {(deletedOn || availableUntil) && (
        <dl className="mt-4 w-full max-w-sm space-y-1 text-left text-xs text-gray-600">
          {deletedOn && (
            <div className="flex justify-between gap-3" data-testid="auth-soft-deleted-deleted-on">
              <dt className="text-gray-500">{t("auth.account_previously_deleted_date", { defaultValue: "Deleted on" })}</dt>
              <dd className="font-medium text-gray-900">{deletedOn}</dd>
            </div>
          )}
          {availableUntil && (
            <div className="flex justify-between gap-3" data-testid="auth-soft-deleted-available-until">
              <dt className="text-gray-500">{t("auth.account_previously_deleted_available_until", { defaultValue: "Available to restore until" })}</dt>
              <dd className="font-medium text-gray-900">{availableUntil}</dd>
            </div>
          )}
        </dl>
      )}
      {daysLeft !== null && (
        <p className="mt-3 text-xs text-gray-500">
          {t("auth.account_previously_deleted_window", {
            defaultValue: "About {{days}} day(s) left in the restore window.",
            days: daysLeft,
          })}
        </p>
      )}
      {error ? (
        <LiveStatus politeness="assertive" className="mt-4 text-sm font-medium text-red-600">
          {error}
        </LiveStatus>
      ) : null}
      <button
        type="button"
        onClick={onRestore}
        disabled={loading}
        data-testid="auth-restore-account"
        className="primary-action mt-6 inline-flex h-11 w-full max-w-xs items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {t("auth.restore_my_account", { defaultValue: "Restore my account" })}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="mt-3 inline-flex h-11 w-full max-w-xs items-center justify-center rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {t("auth.cancel_keep_deleted", { defaultValue: "Cancel (keep account deleted)" })}
      </button>
    </div>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="relative my-3">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
      <div className="relative flex justify-center"><span className="bg-white px-3 text-xs font-medium text-gray-500">{label}</span></div>
    </div>
  );
}

function SocialButtons({ onSocial, disabled }: { onSocial: (p: SocialProvider) => void; disabled: boolean }) {
  const legacySocialEnabled = useFeatureFlag("feature_apple_google_signin");
  const googleEnabled = useFeatureFlag("feature_google_signin") || legacySocialEnabled;
  const appleEnabled = useFeatureFlag("feature_apple_signin") || legacySocialEnabled;
  const providers = SOCIAL_PROVIDERS.filter((provider) =>
    provider === "google" ? googleEnabled :
    provider === "apple" ? appleEnabled :
    true,
  );
  // Cast through `number` so the literal-tuple length doesn't make the
  // 2/3 branches unreachable at the type level if the SOCIAL_PROVIDERS
  // tuple is ever resized.
  const providerCount: number = providers.length;
  const cols =
    providerCount === 2
      ? "grid-cols-2"
      : providerCount >= 3
        ? "grid-cols-3"
        : "grid-cols-1";
  return (
    <div className={cn("grid gap-3", cols)}>
      {providers.map((p) => (
        <SocialTile key={p} provider={p} onClick={() => onSocial(p)} disabled={disabled} />
      ))}
    </div>
  );
}

function SocialTile({ provider, onClick, disabled }: { provider: SocialProvider; onClick: () => void; disabled: boolean }) {
  const { t } = useTranslation();
  let icon: ReactNode;
  switch (provider) {
    case "google":
      icon = <GoogleLogo className="h-7 w-7" />;
      break;
    case "facebook":
      icon = <DsIcon name="facebook" className="h-7 w-7 text-[#1877F2]" />;
      break;
    case "apple":
      icon = <AppleLogo className="h-7 w-7" />;
      break;
  }
  const label = providerLabel(provider, t);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex h-16 items-center justify-center rounded-xl border border-gray-300 bg-white transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {icon}
    </button>
  );
}

function FloatingField({
  id,
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  autoFocus,
  autoComplete,
  required,
  inputMode,
  maxLength,
  suffix,
  inputRef,
  invalid,
}: {
  id?: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  autoComplete?: string;
  required?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  suffix?: ReactNode;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  invalid?: boolean;
}) {
  const reactId = useId();
  const fieldId = id ?? reactId;
  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={fieldId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={invalid || undefined}
        placeholder=" "
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        className={cn(
          "peer block w-full rounded-xl border bg-white px-4 pb-2 pt-6 text-base text-gray-900 transition-colors placeholder-transparent focus:outline-none focus:ring-1",
          invalid
            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
            : "border-gray-400 focus:border-brand focus:ring-brand",
          suffix && "pr-12",
        )}
      />
      <label
        htmlFor={fieldId}
        className={cn(
          "pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base transition-[top,transform,font-size] peer-focus:top-3 peer-focus:translate-y-0 peer-focus:text-xs peer-[&:not(:placeholder-shown)]:top-3 peer-[&:not(:placeholder-shown)]:translate-y-0 peer-[&:not(:placeholder-shown)]:text-xs",
          invalid ? "text-red-600" : "text-gray-500",
        )}
      >
        {label}
      </label>
      {suffix ? (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">{suffix}</div>
      ) : null}
    </div>
  );
}

function SocialButton({ provider, onClick, disabled }: { provider: SocialProvider; onClick: () => void; disabled: boolean }) {
  const { t } = useTranslation();
  let icon: ReactNode;
  switch (provider) {
    case "google":
      icon = <GoogleLogo className="h-4 w-4" />;
      break;
    case "facebook":
      icon = <DsIcon name="facebook" className="h-4 w-4 text-[#1877F2]" />;
      break;
    case "apple":
      icon = <AppleLogo className="h-4 w-4" />;
      break;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="relative flex h-12 w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="absolute left-4 inline-flex h-5 w-5 items-center justify-center">{icon}</span>
      <span className="text-center">{providerLabel(provider, t)}</span>
    </button>
  );
}

interface ExtendedProfile {
  id?: number | string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  marketing_opt_in?: boolean | null;
  terms_accepted_at?: string | null;
  missing_profile_fields?: string[];
  missing_fields?: string[];
  is_complete?: boolean;
  email_is_relayed?: boolean;
  isDriveShareOrigin?: boolean;
  // Task #6837 (F) — re-consent flow. Set by ensure-user when the
  // `terms-reconsent` flag is ON and a material Terms/Privacy revision
  // post-dates the user's latest acceptance.
  needs_reconsent?: boolean;
  legal_reconsent?: { terms_at: string | null; privacy_at: string | null } | null;
}

/**
 * Reads the post-auth landing path written by `rememberPostAuthNext` and
 * navigates to it once the user is fully signed in AND their profile is
 * complete. This is the React-state-driven replacement for the legacy
 * `setTimeout(800)` polls inside AuthModal — those raced with
 * AuthProvider's ensure-user round-trip and either forced a manual page
 * refresh or navigated away before ProfileCompletionDialog could mount.
 *
 * Mounted alongside ProfileCompletionWatcher inside AuthModalProvider so
 * it lives at the same scope as every consumer of useAuthModal — there is
 * exactly one instance per app, and it cannot accidentally be unmounted
 * mid-flow by a route change.
 */
function PostAuthRedirector() {
  const { user, profile } = useUser();
  const ext = profile as unknown as ExtendedProfile | null;
  // Task #1763: only an explicit `is_complete: true` counts as ready to
  // redirect. Mirrors the inverse predicate in ProfileCompletionWatcher
  // — a missing/undefined `is_complete` (e.g. an undecorated server
  // response from the ensure-user race-recovery path) must keep us in
  // the "not yet complete" state instead of letting the redirector race
  // ahead of the completion dialog.
  const profileComplete = !!user && user.app_metadata?.checkout_guest !== true && !!ext && ext.is_complete === true;
  // Task #5620 — during a password-recovery flow the user holds a live
  // (recovery) session, so without this guard PostAuthRedirector would treat
  // it like a normal sign-in and could carry them off to a stashed `next` /
  // dashboard before they set a new password. RecoveryModeRedirector owns
  // navigation while recovery mode is active; stand down here.
  const recoveryMode = useRecoveryMode();
  const [location, setLocation] = useLocation();
  useEffect(() => {
    if (recoveryMode) return;
    // A provider callback can mount lazily after the persisted session/profile.
    // Never let the global redirector bypass the callback's code exchange,
    // identity checks or profile setup by consuming its saved destination.
    if (location.split("?")[0] === "/auth/callback") return;
    if (!profileComplete) return;
    let target: string | null = null;
    try { target = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY); } catch { /* noop */ }
    if (!target) return;
    try { sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY); } catch { /* noop */ }
    const safe = safeRedirectPath(target);
    if (safe && safe !== "/") {
      const checkout = checkoutHandoffContext(safe);
      if (checkout) {
        track("checkout_auth_handoff", {
          checkout_journey_id: checkout.checkoutJourneyId,
          car_id: checkout.carId,
          stage: "authentication",
          outcome: "succeeded",
          method: "unknown",
        });
      }
      setLocation(postAuthWouterDestination(safe));
    }
  }, [profileComplete, setLocation, recoveryMode, location]);
  return null;
}

// Task #5287 — synthesize the minimal ExtendedProfile the completion dialog
// needs when WEB deferral means there is no public.users row yet (profile is
// null but the principal is authenticated). Name defaults come from the auth
// user's provider metadata so OAuth users see their name pre-filled (and
// locked); email comes from the auth user when present. `missing_profile_fields`
// drives the dialog's needsName/needsEmail — first/last are "missing" only when
// the provider didn't supply them, email only when the principal has none. The
// row is created by /auth/complete-profile on submit.
function synthesizeDeferredProfile(
  user: { email?: string | null; user_metadata?: { first_name?: string; last_name?: string } | null } | null,
): ExtendedProfile {
  const pf = user?.user_metadata?.first_name?.trim() || "";
  const pl = user?.user_metadata?.last_name?.trim() || "";
  const email = user?.email || "";
  const missing: ExtendedProfile["missing_profile_fields"] = [];
  if (!pf) missing.push("first_name");
  if (!pl) missing.push("last_name");
  if (!email) missing.push("email");
  return {
    first_name: pf,
    last_name: pl,
    email,
    email_is_relayed: /@privaterelay\.appleid\.com$/i.test(email),
    missing_profile_fields: missing,
    is_complete: false,
  } as unknown as ExtendedProfile;
}

function ProfileCompletionWatcher({ deferForDriveShare = false }: { deferForDriveShare?: boolean } = {}) {
  const { user, profile, session, retryProfile, needsProfile } = useUser();
  const [location] = useLocation();
  const search = useSearch();
  // A post-booking email proof may create a new provider identity with no
  // customer row yet. Its explicit completion form belongs in the booking,
  // not in the unrelated, blocking global signup dialog.
  if (guestProfileCompletionFor(user) && (needsProfile || profile?.is_complete !== true)) return null;
  // Fresh checkout owns inline completion; every other protected route keeps
  // the shared dialog. No global policy or API completion rule is weakened.
  if (location.replace(/\/+$/, "") === "/checkout" && !isPrivateCheckoutEntry(search, user?.id)) return null;
  const ext = profile as unknown as ExtendedProfile | null;
  // Task #1763: defensive — treat anything other than an explicit
  // `is_complete: true` as incomplete. The ensure-user race-recovery
  // branch used to return an undecorated row with `is_complete:
  // undefined`; the strict `=== false` check silently no-op'd and the
  // dialog never mounted until the user manually refreshed. With this
  // change, even if the server omits the flag (any future regression),
  // we still render the completion dialog rather than stranding a
  // brand-new user without setup.
  const incompleteExisting = !!user && !!ext && ext.is_complete !== true;
  // Task #6837 (F) — an already-complete profile may still owe a re-accept
  // after a material Terms/Privacy change (button-notice, no checkbox). Only
  // mount the re-consent variant when the profile is otherwise complete so it
  // never competes with the finish-setup flow.
  const needsReconsent = !!user && !!ext && ext.is_complete === true && ext.needs_reconsent === true;
  if (needsReconsent && !deferForDriveShare) {
    return <ProfileCompletionDialog session={session} profile={ext} onDone={retryProfile} reconsentMode />;
  }
  // Task #5287 — WEB deferral: the principal is authenticated but has no local
  // row yet. There is no profile to inspect, so mount the dialog off
  // needsProfile and synthesize the fields it reads. Submitting it creates the
  // row via /auth/complete-profile and clears needsProfile.
  const needsCreate = !!user && needsProfile && !ext;
  // Task #4765 — defer to the DriveShare login nudge while it owns the screen so
  // a migrated DriveShare user is nudged to leave BEFORE being asked to finish a
  // BAC profile, and the two modals never stack. Once the nudge is dismissed,
  // `deferForDriveShare` flips false and the completion dialog renders normally.
  if ((!incompleteExisting && !needsCreate) || deferForDriveShare) return null;
  const dialogProfile = ext ?? synthesizeDeferredProfile(user);
  return <ProfileCompletionDialog session={session} profile={dialogProfile} onDone={retryProfile} newAccountSetup={needsCreate} />;
}

function ProviderBadge({ provider }: { provider: string }) {
  const { t } = useTranslation();
  if (provider === "google") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
        <GoogleLogo className="h-3.5 w-3.5" /> {t("auth.signed_up_google")}
      </span>
    );
  }
  if (provider === "apple") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
        <AppleLogo className="h-3.5 w-3.5" /> {t("auth.signed_up_apple")}
      </span>
    );
  }
  if (provider === "facebook") {
    // Task #4226 — Facebook sign-in is retired; the badge reflects the
    // retired state rather than implying a working "Continue with Facebook".
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
        <DsIcon name="facebook" className="h-3.5 w-3.5 text-[#1877F2]" />{" "}
        {t("auth.signed_up_facebook_retired", { defaultValue: "Facebook sign-in retired. Use your email." })}
      </span>
    );
  }
  return null;
}

function detectProvider(user: { app_metadata?: { provider?: string } | null } | null): string | null {
  const p = user?.app_metadata?.provider;
  if (p === "google" || p === "apple" || p === "facebook") return p;
  return null;
}

function ProfileCompletionFrame({ embedded, ...props }: ComponentProps<typeof ModalShell> & { embedded?: boolean }) {
  const { t } = useTranslation();
  if (!embedded) return <ModalShell {...props} />;
  return <section aria-label={t("checkout.identity.heading")} className="mx-auto w-full max-w-lg rounded-2xl border border-border bg-background p-5 sm:p-6" data-testid="guest-inline-profile-completion">
    {props.children}
  </section>;
}

export function GuestProfileCompletionForm({ details }: { details: GuestProfileCompletion }) {
  const { user, session, profile, needsProfile, retryProfile } = useUser();
  if (!user || !session) return null;
  const existing = needsProfile ? null : profile as ExtendedProfile | null;
  return <ProfileCompletionDialog session={session}
    profile={existing ?? synthesizeDeferredProfile(user)}
    onDone={retryProfile} newAccountSetup={!existing} embedded initialDetails={details} />;
}

export function ProfileCompletionDialog({ session, profile, onDone, reconsentMode = false, newAccountSetup = false, embedded = false, initialDetails }: { session: { access_token?: string } | null; profile: ExtendedProfile; onDone: () => void; reconsentMode?: boolean; newAccountSetup?: boolean; embedded?: boolean; initialDetails?: GuestProfileCompletion }) {
  const { t } = useTranslation();
  const { user, signOut } = useUser();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const missing = profile.missing_profile_fields || profile.missing_fields || [];
  const needsName = missing.includes("first_name") || missing.includes("last_name");
  const needsEmail = missing.includes("email") && !user?.email;
  // Terms consent is captured implicitly on Continue; no `needsTerms` branch.
  const isAppleRelay = !!profile.email_is_relayed;

  const [firstName, setFirstName] = useState(profile.first_name || initialDetails?.firstName || "");
  const [lastName, setLastName] = useState(profile.last_name || initialDetails?.lastName || "");
  const [email, setEmail] = useState(profile.email || "");
  useEffect(() => {
    if (!user?.email_confirmed_at || !user.email || user.app_metadata?.checkout_guest === true) return;
    if (profile.first_name && profile.last_name) return;
    const controller = new AbortController();
    void import("@/lib/guest-checkout-api").then(async ({ guestCheckoutRequest }) => {
      const { guest } = await guestCheckoutRequest<{
        guest: { email: string; firstName: string; lastName: string } | null;
      }>("", { signal: controller.signal });
      if (controller.signal.aborted || !guest || guest.email.toLowerCase() !== user.email!.toLowerCase()) return;
      // Prefill only missing fields after genuine mailbox ownership. The
      // customer still reviews and explicitly saves; existing names win.
      if (!profile.first_name) setFirstName((current) => current || guest.firstName);
      if (!profile.last_name) setLastName((current) => current || guest.lastName);
    }).catch(() => { /* Optional prefill; the editable form remains usable. */ });
    return () => controller.abort();
  }, [user?.id, user?.email, user?.email_confirmed_at, profile.first_name, profile.last_name]);
  const [marketingOptIn, setMarketingOptIn] = useState(embedded ? profile.marketing_opt_in === true : true);
  // Separate opt-out for the Ghost blog newsletter — defaults ON (subscribed)
  // so users are subscribed by default and must untick to opt out, mirroring
  // the general marketing opt-out above. The backend subscribes whenever
  // `blog_opt_in === true`, so a pre-checked box sends true.
  const [blogOptIn, setBlogOptIn] = useState(!embedded);
  // Explicit terms acceptance — user must tick the checkbox to continue.
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After the API attaches the captured email and queues a verification
  // message, switch to a "check your email" view so the user knows what to do
  // next. The submitted email is captured so we can show it back to them.
  const [verificationStep, setVerificationStep] = useState<{
    email: string;
    sent: boolean;
  } | null>(null);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [resendError, setResendError] = useState<string | null>(null);
  // Logo shortcut-home: setup is still incomplete here, so tapping the brand
  // logo must confirm before abandoning the in-progress profile completion.
  // Confirming signs the user out (otherwise ProfileCompletionWatcher would
  // immediately re-mount this blocking dialog) and routes home; they can
  // finish setup next time they sign in.
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  const leaveSetupForHome = useCallback(async () => {
    setConfirmLeaveOpen(false);
    try { await signOut(); } catch { /* noop */ }
    setLocation("/");
  }, [signOut, setLocation]);

  const provider = useMemo(() => detectProvider(user), [user]);

  const providerFirstName = (user?.user_metadata as { first_name?: string } | undefined)?.first_name?.trim() || "";
  const providerLastName = (user?.user_metadata as { last_name?: string } | undefined)?.last_name?.trim() || "";
  const firstNameLocked = !!provider && (!!providerFirstName || !!profile.first_name?.trim());
  const lastNameLocked = !!provider && (!!providerLastName || !!profile.last_name?.trim());

  const apiBase = import.meta.env.BASE_URL || "/";

  const submit = useCallback(async () => {
    setError(null);
    if (needsName && (!firstName.trim() || !lastName.trim())) {
      setError(t("auth.name_required") || "Please enter your name");
      return;
    }
    if (needsEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t("auth.invalid_email") || "Please enter a valid email");
      return;
    }
    if (!acceptTerms) {
      setError(t("auth.accept_terms") || "Please accept the terms of service");
      return;
    }
    if (!session?.access_token) {
      setError(t("auth.missing_session"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}api/auth/complete-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: needsEmail ? email.trim().toLowerCase() : undefined,
          marketing_opt_in: marketingOptIn,
          blog_opt_in: blogOptIn,
          accept_terms: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || t("auth.save_profile_failed"));
        setLoading(false);
        return;
      }
      try { sessionStorage.removeItem("ensure_user_cache"); } catch {}

      // If the API queued a verification email (FB-no-email path), keep the
      // dialog open and pivot to the "Check your email" view rather than
      // closing immediately. The profile is already complete enough for the
      // app to function — the user just needs to confirm their address.
      if (data?.pending_email_verification) {
        const submittedEmail = needsEmail ? email.trim().toLowerCase() : (profile.email || "");
        setVerificationStep({ email: submittedEmail, sent: data?.verification_email_sent !== false });
        setLoading(false);
        // Refresh profile in the background so RequireAuth no longer blocks.
        onDone();
        return;
      }

      onDone();
      toast({ title: t("auth.toast_all_set_title"), description: t("auth.toast_all_set_desc") });

      let target: string | null = null;
      try { target = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY); } catch {}
      try { if (target) sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY); } catch {}
      const safe = safeRedirectPath(target);
      if (safe !== "/") setLocation(postAuthWouterDestination(safe));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.save_profile_failed"));
    } finally {
      setLoading(false);
    }
  }, [firstName, lastName, email, marketingOptIn, blogOptIn, acceptTerms, needsName, needsEmail, session, apiBase, onDone, t, toast, setLocation, profile.email]);

  const resendVerification = useCallback(async () => {
    if (!session?.access_token) return;
    setResendError(null);
    setResendState("sending");
    try {
      const res = await fetch(`${apiBase}api/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResendError(data?.error || t("auth.resend_failed"));
        setResendState("error");
        return;
      }
      if (data?.already_verified) {
        // Email is already verified; close the dialog and let the app continue.
        onDone();
        toast({ title: t("auth.toast_verified_title"), description: t("auth.toast_verified_desc") });
        return;
      }
      setResendState(data?.sent === false ? "error" : "sent");
      if (data?.sent === false) {
        setResendError(t("auth.resend_try_again"));
      }
    } catch (err) {
      setResendError(err instanceof Error ? err.message : t("auth.resend_failed"));
      setResendState("error");
    }
  }, [session, apiBase, onDone, toast, t]);

  // Task #6837 (F) — re-consent accept. Button-notice (no checkbox): tapping
  // "I agree" records a fresh acceptance and refreshes the profile, which
  // clears `needs_reconsent` and dismisses this dialog.
  const acceptReconsent = useCallback(async () => {
    setError(null);
    if (!session?.access_token) {
      setError(t("auth.missing_session"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}api/auth/accept-terms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || t("auth.save_profile_failed"));
        setLoading(false);
        return;
      }
      try { sessionStorage.removeItem("ensure_user_cache"); } catch {}
      onDone();
      toast({ title: t("auth.reconsent_toast_title"), description: t("auth.reconsent_toast_desc") });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.save_profile_failed"));
    } finally {
      setLoading(false);
    }
  }, [session, apiBase, onDone, t, toast]);

  const continueAfterVerification = useCallback(() => {
    onDone();
    let target: string | null = null;
    try { target = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY); } catch {}
    try { if (target) sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY); } catch {}
    const safe = safeRedirectPath(target);
    if (safe !== "/") setLocation(postAuthWouterDestination(safe));
  }, [onDone, setLocation]);

  if (verificationStep) {
    return (
      <ModalShell
        open
        title={t("auth.check_your_email") || "Check your email"}
        titleAlign="center"
        showClose={false}
        closeOnBackdrop={false}
        closeOnEscape={false}
        size="md"
        zIndex={60}
        data-testid="profile-completion-verify"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 sm:h-20 sm:w-20">
            <Mail className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={1.6} />
          </div>
          <h3 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
            {t("auth.check_your_email")}
          </h3>
          <p className="mt-2 max-w-sm text-sm text-gray-600 sm:text-[15px]">
            <Trans
              i18nKey="auth.verify_sent_to"
              values={{ email: verificationStep.email }}
              components={{ strong: <strong className="font-medium text-gray-900" /> }}
            />
          </p>
          <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
            <Mail className="h-4 w-4 shrink-0 text-gray-500" strokeWidth={1.8} />
            <span className="truncate">{verificationStep.email}</span>
          </div>

          {!verificationStep.sent ? (
            <Alert className="mt-5 text-left">
              <Info className="h-4 w-4" />
              <AlertDescription>{t("auth.verify_send_failed")}</AlertDescription>
            </Alert>
          ) : null}

          <p className="mt-5 max-w-sm text-sm text-gray-600">{t("auth.verify_check_spam")}</p>

          {resendError ? (
            <div className="mt-4 w-full max-w-sm rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{resendError}</div>
          ) : null}
          {resendState === "sent" ? (
            <div className="mt-4 w-full max-w-sm rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
              {t("auth.verify_resent_confirmation")}
            </div>
          ) : null}

          <div className="mt-6 flex w-full max-w-sm flex-col gap-3">
            <button
              type="button"
              onClick={continueAfterVerification}
              className="primary-action cursor-pointer flex h-12 w-full items-center justify-center rounded-lg px-4 text-sm font-semibold transition-colors"
            >
              {t("auth.continue_to_brand")}
            </button>
            <button
              type="button"
              onClick={resendVerification}
              disabled={resendState === "sending"}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendState === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("auth.resend_email")}
            </button>
          </div>

          <p className="mt-4 max-w-xs text-center text-xs text-muted-foreground">
            {t("auth.verify_keep_browsing")}
          </p>
        </div>
      </ModalShell>
    );
  }

  if (reconsentMode) {
    return (
      <ModalShell
        open
        title={<span className="sr-only">{t("auth.reconsent_title")}</span>}
        titleAlign="center"
        showClose={false}
        closeOnBackdrop={false}
        closeOnEscape={false}
        size="md"
        zIndex={60}
        hideHeaderDivider
        bodyClassName="px-6 py-6 sm:px-8 sm:py-8"
        data-testid="terms-reconsent"
      >
        <div className="space-y-6">
          <div className="flex flex-col items-center text-center">
            <Logo size="md" />
            <h2 className="mt-4 text-[22px] font-semibold leading-tight text-gray-900 sm:text-[26px]">
              {t("auth.reconsent_title")}
            </h2>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              {t("auth.reconsent_body")}
            </p>
          </div>

          {error ? (
            <div className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}

          <p className="text-center text-xs text-muted-foreground">
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">
              {t("auth.reconsent_review_terms")}
            </a>
            {" · "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">
              {t("auth.reconsent_review_privacy")}
            </a>
          </p>

          <button
            type="button"
            onClick={acceptReconsent}
            disabled={loading}
            className="primary-action flex h-12 w-full items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t("auth.reconsent_agree")}
          </button>
        </div>
      </ModalShell>
    );
  }

  const emailReadOnlyValue = profile.email || user?.email || "";
  const setupTitle = embedded ? t("checkout.identity.title", { defaultValue: "Your details" })
    : t(newAccountSetup ? "auth.new_account_setup_title" : "auth.finish_setup_title_short");
  return (
    <ProfileCompletionFrame
      embedded={embedded}
      open
      title={<span className="sr-only">{setupTitle}</span>}
      titleAlign="center"
      showClose={false}
      closeOnBackdrop={false}
      closeOnEscape={false}
      size="md"
      zIndex={60}
      hideHeaderDivider
      bodyClassName="px-6 py-6 sm:px-8 sm:py-8"
      data-testid="profile-completion"
    >
      <div className="space-y-6">
        <div className="flex flex-col items-center text-center">
          {!embedded && <button
            type="button"
            onClick={() => setConfirmLeaveOpen(true)}
            aria-label={t("auth.go_to_home_page")}
            className="mb-4 inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            <Logo size="md" />
          </button>}
          <h2 className="text-[22px] font-semibold leading-tight text-gray-900 sm:text-[26px]">
            {setupTitle}
          </h2>
          {newAccountSetup && !embedded && (
            <div className="mt-3 space-y-2 text-sm text-gray-600">
              <p>{t("auth.new_account_setup_description")}</p>
              <p>{t("auth.new_account_setup_existing_guidance")}</p>
            </div>
          )}
        </div>

        <AlertDialog open={confirmLeaveOpen} onOpenChange={setConfirmLeaveOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("auth.leave_setup_title")}</AlertDialogTitle>
              <AlertDialogDescription>{t("auth.leave_setup_desc")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("auth.leave_setup_cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={leaveSetupForHome}>
                {t("auth.leave_setup_confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {(provider || isAppleRelay) ? (
          <div className="space-y-3">
            {provider && !isAppleRelay && <ProviderBadge provider={provider} />}
            {isAppleRelay && (
              <Alert>
                <AlertDescription>
                  {t("auth.apple_relay_notice")}
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-4"
        >
          {error ? (
            <div className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}

          {/* Grouped name card — outer ring + inner divider, focus-within darkens. */}
          <div className="grid grid-cols-2 divide-x divide-gray-300 overflow-hidden rounded-xl border border-gray-300 transition-colors focus-within:border-gray-900 focus-within:ring-1 focus-within:ring-gray-900">
            <label className="flex h-14 flex-col justify-center px-3">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-700">{t("auth.first_name")}</span>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                readOnly={firstNameLocked}
                disabled={firstNameLocked}
                className="w-full border-0 bg-transparent p-0 text-base leading-tight text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:text-gray-500"
              />
            </label>
            <label className="flex h-14 flex-col justify-center px-3">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-700">{t("auth.last_name")}</span>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                readOnly={lastNameLocked}
                disabled={lastNameLocked}
                className="w-full border-0 bg-transparent p-0 text-base leading-tight text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:text-gray-500"
              />
            </label>
          </div>

          {/* Email — same grouped-input language; muted bg when read-only. */}
          {needsEmail ? (
            <div className="space-y-1">
              <label className="flex h-14 flex-col justify-center rounded-xl border border-gray-300 px-3 transition-colors focus-within:border-gray-900 focus-within:ring-1 focus-within:ring-gray-900">
                <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-700">{t("auth.email")}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full border-0 bg-transparent p-0 text-base leading-tight text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                />
              </label>
              <span className="block px-1 text-xs text-gray-500">
                {t("auth.email_verify_hint")}
              </span>
            </div>
          ) : (
            <label className="flex h-14 flex-col justify-center rounded-xl border border-gray-200 bg-gray-50 px-3">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-600">{t("auth.email")}</span>
              <input
                type="email"
                value={emailReadOnlyValue}
                disabled
                readOnly
                className="w-full border-0 bg-transparent p-0 text-base leading-tight text-gray-500 focus:outline-none focus:ring-0"
              />
            </label>
          )}

          <label className="flex items-start gap-3 pt-1 text-[13px] leading-snug text-gray-700">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
              data-testid="profile-completion-accept-terms"
            />
            <span>
              {t("auth.terms_agree")}{" "}
              <Link
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t("auth.terms_of_service")} (${t("auth.opens_in_new_tab")})`}
                className="font-medium text-gray-900 underline underline-offset-2 hover:text-gray-700"
              >
                {t("auth.terms_of_service")}
              </Link>{" "}
              {t("auth.and")}{" "}
              <Link
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t("auth.privacy_policy")} (${t("auth.opens_in_new_tab")})`}
                className="font-medium text-gray-900 underline underline-offset-2 hover:text-gray-700"
              >
                {t("auth.privacy_policy")}
              </Link>.
            </span>
          </label>

          <label className="flex items-start gap-3 text-[13px] leading-snug text-gray-600">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
            />
            <span>{t("auth.marketing_opt_in_modal")}</span>
          </label>

          <label
            className="flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/5 px-3.5 py-3 text-[13px] leading-snug text-gray-700"
            data-testid="profile-completion-blog-opt-in-box"
          >
            <input
              type="checkbox"
              checked={blogOptIn}
              onChange={(e) => setBlogOptIn(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
              data-testid="profile-completion-blog-opt-in"
            />
            <span>{t("auth.blog_opt_in_modal")}</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="primary-action flex h-14 w-full items-center justify-center rounded-xl px-4 text-base font-semibold shadow-sm transition-[background-color,box-shadow] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t(newAccountSetup && !embedded ? "auth.new_account_setup_submit" : "auth.finish_setup_btn")}
          </button>

          <button
            type="button"
            onClick={() => signOut()}
            className="block w-full text-center text-[13px] text-muted-foreground transition-colors hover:text-foreground hover:underline hover:underline-offset-2"
          >
            {t("auth.use_different_account")}
          </button>
        </form>
      </div>
    </ProfileCompletionFrame>
  );
}
